// src/dq/SdhReviewWorkspace.tsx
// DQ Review cockpit — rule-centric SME decision surface.
// One count system (rules, not tasks). Impact shown inline on every card. Evidence on the card.
// Reads/writes exclusively through sdhAPI (/adriva_api/dq/sdh). Drop-in replacement — same props.
import { saOf, ruleKey, SA_STORE } from "./setasideStore";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SmeHelp from "./SmeHelp";
import DashboardTab from "./DashboardTab";
import { HelpProvider, HelpDot, Pill, dimensionOf } from "./HelpKit";
import { useAuth } from "../hooks/useAuth";
import SmeImportPanel from "./SmeImportPanel";
import IterationTab from "./IterationTab";
import RuleStudioPanel from "./RuleStudioPanel";
import FieldValuesPanel from "./FieldValuesPanel";
import RuleVersionsPanel from "./RuleVersionsPanel";
import SampleRecordsPanel from "./SampleRecordsPanel";
import { matchBucket, LIFE, lifeOf, type Bucket } from "./RoleViews";
import { usePolicies, policyOfRule, PolicyPopup } from "./PoliciesView";
import RuleLogicView, { FlagBadges, flagsOf, flagNotesOf, HIDDEN_FLAGS, FLAG_LABEL, ProposalBadge, ProposalBanner, pendingProposal } from "./RuleLogicView";
import { measurementOf, NotMeasured, impactText } from "./measurement";
import SdhFieldView from "./SdhFieldView";
import { sdh_setaside,
  sdh_rules, sdh_applyDecisions, sdh_ruleDetail, sdh_preview, sdh_completeness,
  sdh_dataProfile, sdh_dataProfileColumn, sdh_conformanceHistory, sdh_exportRulebook,
  type EnrichedRule, type ProfileTable, type ProfileColumn, type ProfileColumnDetail,
  type ConformanceRun, type CompletenessFinding,
  SME_ASSESSMENTS, ASSESSMENT_BY_VALUE, GATE_TONE, sdh_smeTaxonomy, type AssessmentDef,
} from "../api/sdhAPI";

/* ─────────────────────────── helpers ─────────────────────────── */
const fmt = (n?: number | null) => (n == null ? "—" : Number(n).toLocaleString());
const pctf = (x?: number | null) => (x == null ? "—" : `${(x * 100).toFixed(x > 0 && x < 0.999 ? 1 : 0)}%`);

type Scope = "cross-table" | "cross-field" | "single";
// ── Persisted state — survives remounts, reloads, and modal open/close ────────
// The SME's tab, filters, search and scroll must stay put no matter what happens
// (refine, decide, comment). We mirror the state to sessionStorage keyed per entity
// so it is restored even if the component tree remounts.
function usePersistedState<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [val, setVal] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(val)); } catch { /* quota / private mode */ }
  }, [key, val]);
  return [val, setVal];
}

function scopeOf(r: EnrichedRule): Scope {
  const raw: any = r._raw || {};
  const pf: any[] = raw.participating_fields || [];
  const tables = new Set<string>();
  const fields = new Set<string>();
  const own = String(r.owner_field || raw.owner_field_id || "");
  if (own.includes("::")) tables.add(own.split("::")[0].toUpperCase());
  pf.forEach((p) => {
    const fid = String(p.field_id || "");
    if (fid) fields.add(fid.toUpperCase());
    if (fid.includes("::")) tables.add(fid.split("::")[0].toUpperCase());
  });
  if (String(raw.execution_scope || r.execution_scope) === "cross_table" || tables.size >= 2) return "cross-table";
  return fields.size >= 2 ? "cross-field" : "single";
}
const FAM_LABEL: Record<string, string> = {
  conditional_mandatory: "Conditional", conditional_exclusion: "Exclusion", conditional_inclusion: "Inclusion",
  code_domain: "Value set", comparison: "Comparison", referential: "Reference", mandatory: "Mandatory",
  range: "Range", uniqueness: "Uniqueness", mutual_exclusion: "Exclusion", unit_conversion: "Unit",
  formula: "Formula", completeness: "Data gap",
};
type Auth = "confirmed" | "observed" | "inferred" | "none";
function authOf(r: EnrichedRule): Auth {
  const raw: any = r._raw || {};
  const la = (r as any).rule_logic_authority || raw.rule_logic_authority;
  if (la === "client_confirmed") return "confirmed";
  const ps = (r as any).policy_status || raw.policy_status;
  if (ps === "backed_confirmed") return "confirmed";
  // deterministic/structural rules are derived from observed profiling data — treat as observed, not "no policy"
  if (ps === "backed_observed" || ps === "structural_no_policy" || la === "observed") return "observed";
  if (ps === "backed_inferred" || la === "inferred") return "inferred";
  return "none";
}
// UI.3.2 — created_by values, in the reviewer's language. Unmapped values fall back to the
// raw token lower-cased, so a new producer shows up rather than disappearing.
const MAKER_LABEL: Record<string, string> = {
  FIND_DEPENDENCY: "the discovery agent (LLM)",
  SDH_G4_5_EK_INGEST: "knowledge ingest (from an EKD dependency)",
  SDH_G3_FOUNDATIONAL: "profiling (deterministic)",
  SDH_G3: "profiling (deterministic)",
  RULE_DEDUP: "dedup merge",
  SME_REFINE: "agent refinement",
  CLIENT_IMPORT: "your reviewed workbook",
  BACKFILL: "backfill (author not recorded at the time)",
  unknown_pre_migration: "not recorded (pre-migration)",
};

const AUTH_LABEL: Record<Auth, string> = { confirmed: "client-confirmed", observed: "observed", inferred: "inferred", none: "no policy" };
const lifecycleOf = (s?: string) =>
  ({ AUTO_APPROVED: "Approved", SME_APPROVED: "Approved", PENDING: "Draft", REJECTED: "Rejected" } as any)[s || ""] || "Draft";

// Single source for turning a chosen assessment into its decision gate — used by the counts preview,
// the card tint, the tray breakdown and the save payload, so gate logic lives in exactly one place.
const gateOf = (assessment?: string): "" | "APPROVED" | "PENDING" | "REJECTED" =>
  (assessment && (ASSESSMENT_BY_VALUE[assessment]?.gate as any)) || "";

type GateKey = "ALL" | "NOT_REVIEWED" | "PRE_APPROVED" | "APPROVED" | "PENDING" | "REJECTED";
// The rule's PERSISTED gate. Prefers the SME's recorded decision; falls back to the legacy
// approval_status so rules decided before the four-gate rollout still bucket correctly.
const gateOfRule = (r: EnrichedRule): Exclude<GateKey, "ALL"> => {
  // 1. An explicit SME decision always wins.
  const g = (r as any).sme_decision?.decision_gate;
  if (g === "APPROVED" || g === "PENDING" || g === "REJECTED") return g;
  // 2. No SME decision yet — fall back to the AGENT's classification.
  //    AUTO_APPROVED means the agent pre-approved it but a DQ SME has not reviewed it,
  //    so it belongs in "Pre-approved by Agent", NOT in "Approved".
  const status = String(r.approval_status || "").toUpperCase();
  if (status === "AUTO_APPROVED") return "PRE_APPROVED";
  if (status === "REJECTED") return "REJECTED";
  return "NOT_REVIEWED";
};
const GATE_META: { key: GateKey; label: string; tone: string; hint: string }[] = [
  { key: "ALL",          label: "All",          tone: "#42586e", hint: "Every rule in this entity" },
  { key: "NOT_REVIEWED", label: "Needs a decision", tone: "#8a97a3", hint: "Awaiting SME review — refine, approve, reject, or escalate to business" },
  { key: "PRE_APPROVED", label: "Pre-approved", tone: "#0e7c7b", hint: "Validated by the agent but not yet reviewed by a DQ SME" },
  { key: "APPROVED",     label: "Approved",     tone: "#2f7a4f", hint: "Reviewed and approved by DQ SME (and business, if needed)" },
  { key: "PENDING",      label: "Pending business", tone: "#43607e", hint: "Approved by DQ SME, awaiting formal business sign-off" },
  { key: "REJECTED",     label: "Rejected",     tone: "#b23a30", hint: "Will not be taken forward — reason and decider visible" },
];

// Compact card affordance: shows the pending decision (if any) and opens the popover.
function DecideButton({ draft, current, onOpen }:
  { draft?: { assessment: string; comment: string }; current?: any; onOpen: () => void }) {
  const val = draft?.assessment || current?.assessment;
  const def = val ? ASSESSMENT_BY_VALUE[val] : undefined;
  return (
    <button className={"dqc-decide" + (draft ? " pending" : "")}
      style={def ? { ["--t" as any]: GATE_TONE[def.gate] } : undefined} onClick={onOpen}>
      {def ? <><span className="dot" />{def.label}{draft ? " (unsaved)" : ""}</> : "Decide…"}
      <span className="cv">▾</span>
    </button>
  );
}

// The SME's decision control: a compact popover. Two steps — pick the gate, then the reason + a comment.
// A comment is REQUIRED for every decision (including Approve) so nothing is ever recorded without a why.
function DecisionPopover({ value, comment, onCommit, onCancel, anchorLabel, defs }:
  { value?: string; comment?: string; onCommit: (assessment: string, comment: string) => void;
    onCancel: () => void; anchorLabel?: string; defs?: AssessmentDef[] }) {
  const ALL = defs && defs.length ? defs : SME_ASSESSMENTS;
  const [a, setA] = useState<string>(value || "");
  const [c, setC] = useState<string>(comment || "");
  const [g, setG] = useState<string>(value ? (ASSESSMENT_BY_VALUE[value]?.gate as string) : "");
  const opts = ALL.filter((x) => x.gate === g);
  const def = a ? ASSESSMENT_BY_VALUE[a] : undefined;
  const ready = !!a && c.trim().length >= 3;
  const hint = g === "APPROVED" ? "Why is this correct? (recorded with the approval)"
    : g === "PENDING" ? "What needs confirming, and by whom?"
    : "Why is this rule not right?";
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", esc); return () => window.removeEventListener("keydown", esc);
  }, [onCancel]);
  return (
    <>
      <div className="dqc-pop-scrim" onClick={onCancel} />
      <div className="dqc-pop" onClick={(e) => e.stopPropagation()}>
        <div className="dqc-pop-h">{anchorLabel || "Decision"}<button className="x" onClick={onCancel}>×</button></div>
        <div className="dqc-pop-gates">
          {GATE_META.filter((x) => x.key !== "ALL" && x.key !== "NOT_REVIEWED").map((x) => (
            <button key={x.key} className={"gbtn" + (g === x.key ? " on" : "")} style={{ ["--t" as any]: x.tone }}
              onClick={() => { setG(x.key); setA(""); }}>{x.label}</button>
          ))}
        </div>
        {g && (
          <div className="dqc-pop-body">
            <div className="lbl">Reason</div>
            {opts.map((o) => (
              <label key={o.value} className={"ropt" + (a === o.value ? " on" : "")}>
                <input type="radio" name="assess" checked={a === o.value} onChange={() => setA(o.value)} />
                <span>{o.label}{o.condition ? <em> — clears when {o.condition}</em> : null}</span>
              </label>
            ))}
            <div className="lbl req">Comment <b>*</b></div>
            <textarea className="dqc-pop-ta" rows={3} placeholder={hint} value={c} onChange={(e) => setC(e.target.value)} />
            {!ready && <div className="warn">{!a ? "Pick a reason." : "A comment is required (min 3 characters)."}</div>}
          </div>
        )}
        <div className="dqc-pop-f">
          <button className="dqc-btn info" onClick={onCancel}>Cancel</button>
          <button className="dqc-btn app" disabled={!ready}
            style={def ? { background: GATE_TONE[def.gate] } : undefined}
            onClick={() => onCommit(a, c.trim())}>Confirm</button>
        </div>
      </div>
    </>
  );
}

/** The SME's decision, shown wherever a rule appears. Deliberately NO "re-review" prompt: a rule only
 *  changes when the agent revises it, and applying an Excel decision is not a change to the rule. */
function DecisionBadge({ r }: { r: EnrichedRule }) {
  const dec = (r as any).sme_decision || {};
  const gate = dec.decision_gate as keyof typeof GATE_TONE | undefined;
  if (!gate) return null;
  const label = ASSESSMENT_BY_VALUE[dec.assessment]?.label || String(gate);
  const who = dec.sme_name || dec.sme_id;
  const when = dec.decided_at ? String(dec.decided_at).slice(0, 10) : "";
  return (
    <span className="dqc-dec" style={{ ["--t" as any]: GATE_TONE[gate] }}
      title={[label, who && `by ${who}`, when].filter(Boolean).join(" · ")}>
      <span className="dot" />{label}
    </span>
  );
}

type ImpactState = "flags" | "pass" | "unmeasured" | "unsupported" | "not_applicable";
function impactOf(r: EnrichedRule): { state: ImpactState; pass?: number | null; viol?: number | null; appl?: number | null; note?: string | null } {
  // Prefer the pipeline-stamped impact (from the sample run) so cards match the details modal.
  const ia: any = (r as any).review_classification?.impact || (r as any)._raw?.impact_assessment;
  const note: string | null = (r as any).tag_notes?.execution || (r as any).impact_note || ia?.note || ia?.reason || ia?.error || null;
  if (ia && ia.executed) {
    const viol = ia.violations, appl = ia.applicable_rows;
    const pass = ia.violation_rate != null ? 1 - ia.violation_rate : (viol != null && appl ? 1 - viol / appl : 1);
    if (viol != null && viol > 0) return { state: "flags", pass, viol, appl, note };
    return { state: "pass", pass: pass ?? 1, viol: 0, appl, note };
  }
  // Ran, but the rule's condition matched no rows in this extract → not "unmeasured", it's "N/A here".
  if (ia && ia.executed === false && ia.applicable_rows === 0) return { state: "not_applicable", appl: 0, note };
  const st = r.impact_status;
  if (st === "UNSUPPORTED") return { state: "unsupported" };
  const viol = r.impact_violations;
  if (st && viol != null && viol > 0) return { state: "flags", pass: r.impact_pass_rate, viol, appl: r.impact_applicable_rows };
  if (st) return { state: "pass", pass: r.impact_pass_rate ?? 1, viol: 0, appl: r.impact_applicable_rows };
  return { state: "unmeasured" };
}

/* ─────────────────────────── shell ─────────────────────────── */
type TabKey = "review" | "byfield" | "profile" | "dashboard" | "iteration" | "help";
const TABS: { key: TabKey; label: string }[] = [
  { key: "review", label: "Review" },
  { key: "byfield", label: "By field" },
  { key: "profile", label: "Data profile" },
  { key: "dashboard", label: "Dashboard" },
  { key: "iteration", label: "Agent revisions" },
  { key: "help", label: "SME Help" },
];

export default function SdhReviewWorkspace(
  { entityId, entityLabel, appNm }: { entityId: string; entityLabel?: string; appNm: string }
) {
  const [tab, setTab] = usePersistedState<TabKey>(`dq:${entityId}:tab`, "review");
  const [flash, setFlash] = useState<string | null>(null);
  const [detail, setDetail] = useState<EnrichedRule | null>(null);
  const [importing, setImporting] = useState(false);
  const [studioFor, setStudioFor] = useState<EnrichedRule | null>(null);
  const [fieldFor, setFieldFor] = useState<{ table: string; column: string } | null>(null);
  const [versionsFor, setVersionsFor] = useState<EnrichedRule | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Decision vocabulary is fetched ONCE here and shared with the cockpit and the detail modal, so new
  // assessments added on the backend appear everywhere with no UI change.
  const [assessDefs, setAssessDefs] = useState<AssessmentDef[]>(SME_ASSESSMENTS);
  useEffect(() => { sdh_smeTaxonomy().then(setAssessDefs).catch(() => {}); }, []);
  const note = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2600); };

  const openDetail = useCallback(async (ruleId: string) => {
    try { const r = await sdh_ruleDetail(entityId, ruleId); setDetail(r.rule); }
    catch { note("Could not load rule detail"); }
  }, [entityId]);

  const doExport = async () => {
    try { note("Building workbook…"); await sdh_exportRulebook(entityId, appNm); note("Downloaded."); }
    catch { note("Export failed"); }
  };

  return (
    <HelpProvider>
    <div className="dqc">
      <style>{CSS}</style>
      <div className="dqc-top">
        <div>
          <div className="dqc-title">Review rules — {entityLabel || "Product master data"}</div>
          <div className="dqc-sub">Approve the rules that reflect your business logic. Each shows the evidence behind it and what it flags on your live data.</div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="dqc-btn out" onClick={doExport}>⬇ Export rulebook</button>
        <button className="dqc-btn out" onClick={() => setImporting(true)}>⬆ Import reviewed file</button>
      </div>

      <div className="dqc-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={"dqc-tab" + (tab === t.key ? " on" : "")} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="dqc-body">
        {tab === "review" && <ReviewCockpit refreshSignal={reloadKey} entityId={entityId} appNm={appNm} onOpen={openDetail} onNote={note} assessDefs={assessDefs} />}
        {tab === "byfield" && <SdhFieldView entityId={entityId} onOpenRule={openDetail} />}
        {tab === "profile" && <ProfileTab entityId={entityId} />}
        {tab === "dashboard" && <DashboardTab entityId={entityId} onOpen={openDetail} />}
        {tab === "iteration" && <IterationTab entityId={entityId} appNm={appNm} onOpen={openDetail} onNote={note} sessionId={entityId} />}
        {tab === "help" && <SmeHelp />}
      </div>

      {flash && <div className="dqc-flash">{flash}</div>}
      {importing && (
        <SmeImportPanel entityId={entityId} appNm={appNm}
          onClose={() => setImporting(false)}
          onApplied={(m) => { setImporting(false); note(m); setReloadKey((k) => k + 1); }} />
      )}
      {versionsFor && (
        <div className="rd2-over"><RuleVersionsPanel entityId={entityId} appNm={appNm}
          constraintId={versionsFor.rule_id} ruleName={versionsFor.rule_name}
          onClose={() => setVersionsFor(null)}
          onChanged={(m) => { note(m); setDetail(null); setVersionsFor(null); setReloadKey((k) => k + 1); }} /></div>
      )}
      {fieldFor && (
        <div className="rd2-over"><FieldValuesPanel entityId={entityId} table={fieldFor.table} column={fieldFor.column}
          onClose={() => setFieldFor(null)} /></div>
      )}
      {studioFor && (
        <RuleStudioPanel entityId={entityId} appNm={appNm} rule={studioFor}
          onClose={() => setStudioFor(null)}
          onSaved={(m) => { const rid = (studioFor as any)?.rule_id; setStudioFor(null); note(m); setReloadKey((k) => k + 1);
            // keep the detail modal open and refresh it to the new version instead of dumping her to the list
            if (rid) openDetail(rid); }} />
      )}
      {detail && (
        <RuleDetailModal entityId={entityId} appNm={appNm} rule={detail} defs={assessDefs}
          onRefine={() => setStudioFor(detail)}
          onFieldValues={(t, c) => setFieldFor({ table: t, column: c })}
          onVersions={() => setVersionsFor(detail)}
          onClose={() => setDetail(null)} onDone={(m) => { note(m); setReloadKey((k) => k + 1); }} />
      )}
    </div>
    </HelpProvider>
  );
}

/* ─────────────────────────── review cockpit ─────────────────────────── */
type Draft = { assessment: string; comment: string };
// UI.3 — exported, with five OPTIONAL props. Every one defaults to the v1 behaviour, so the
// v1 call site is unchanged and this component renders exactly as before when they are all
// omitted.
//
//   fieldFilter      a fully-qualified TABLE::COLUMN. When set, only rules whose OWNER field
//                    is that field are shown. Note this is NOT the existing `field` state
//                    below — despite its name, `field` holds a TABLE and drives the "Table"
//                    facet. The two are independent and compose.
//   railMode         "inline" (v1: the facet rail sits in the left column of dqc-grid) or
//                    "popover" (v2: the same rail markup, unchanged, floated over the page —
//                    because in v2 the left column belongs to the field list).
//   railOpen /       OPTIONAL controlled open state for the popover. Omit them and the cockpit
//   onRailOpenChange manages it itself and draws its own "Filters" button in the result bar.
//                    Supply them and the HOST owns the trigger — it can live in the shell
//                    header, a toolbar, a keyboard shortcut, anywhere — with no change here.
//   toolbarExtra     arbitrary nodes appended to the cockpit's own toolbar, so the host can
//                    add controls later without this component learning about them.
//   onActiveFilters  fires with the number of active facets whenever it changes, so a host-
//                    owned trigger can render "Filters · 3" without duplicating the logic.
//   onRulesLoaded    fires with the freshly loaded rules on every load, including the reload
//                    after a save. A host that shows its own per-field counts stays in step
//                    with the list instead of drifting the moment decisions are applied.
export function ReviewCockpit({ entityId, appNm, onOpen, onNote, assessDefs, refreshSignal,
                                fieldFilter, railMode = "inline",
                                railOpen: railOpenProp, onRailOpenChange,
                                toolbarExtra, onActiveFilters, onRulesLoaded, lifeFilter, onClearLife, hideGates }:
  { entityId: string; appNm: string; onOpen: (id: string) => void; onNote: (m: string) => void;
    assessDefs: AssessmentDef[]; refreshSignal?: number;
    fieldFilter?: string | null; railMode?: "inline" | "popover";
    railOpen?: boolean; onRailOpenChange?: (open: boolean) => void;
    lifeFilter?: Bucket; onClearLife?: () => void; hideGates?: boolean;
    toolbarExtra?: React.ReactNode; onActiveFilters?: (n: number) => void;
    onRulesLoaded?: (rules: EnrichedRule[]) => void }) {
  const { user } = useAuth();
  const smeId = user?.user_id || user?.email || "unknown";
  const [rules, setRules] = useState<EnrichedRule[]>([]);
  const [counts, setCounts] = useState<{ All: number; Approved: number; Draft: number; Rejected: number }>({ All: 0, Approved: 0, Draft: 0, Rejected: 0 });
  const [decisions, setDecisions] = useState<Record<string, Draft>>({});
  const [previewed, setPreviewed] = useState<Record<string, EnrichedRule>>({});
  const [gate, setGate] = usePersistedState<GateKey>(`dq:${entityId}:gate`, "ALL");
  const [assessFilter, setAssessFilter] = usePersistedState<string | null>(`dq:${entityId}:assess`, null);
  const [popFor, setPopFor] = useState<string | null>(null);
  const [bulkPop, setBulkPop] = useState(false);
  // A reason belonging to another gate must not stay active after switching gates.
  useEffect(() => {
    if (assessFilter && gate !== "ALL" && ASSESSMENT_BY_VALUE[assessFilter]?.gate !== gate) setAssessFilter(null);
  }, [gate, assessFilter]);
  const [field, setField] = usePersistedState<string | null>(`dq:${entityId}:field`, null);
  const [family, setFamily] = usePersistedState<string | null>(`dq:${entityId}:family`, null);
  const [scope, setScope] = usePersistedState<Scope | null>(`dq:${entityId}:scope`, null);
  const [trust, setTrust] = usePersistedState<string | null>(`dq:${entityId}:trust`, null);
  const [risk, setRisk] = usePersistedState<string | null>(`dq:${entityId}:risk`, null);
  const [reason, setReason] = usePersistedState<string | null>(`dq:${entityId}:reason`, null);
  const [onlyViol, setOnlyViol] = usePersistedState<boolean>(`dq:${entityId}:onlyViol`, false);
  // review flags (cannot_fail / contradicted / not_tested / rejected_by_data) are hidden by default
  const [showFlagged, setShowFlagged] = usePersistedState<boolean>(`dq:${entityId}:showFlagged`, false);
  const [onlyLookup, setOnlyLookup] = usePersistedState<boolean>(`dq:${entityId}:onlyLookup`, false);
  const [onlyClientOk, setOnlyClientOk] = usePersistedState<boolean>(`dq:${entityId}:onlyClientOk`, false);
  const [tagFilter, setTagFilter] = usePersistedState<{ lens: string; state: string } | null>(`dq:${entityId}:tagFilter`, null);
  // sidebar sections collapse by default; STATUS stays open (it's the main driver)
  const [openSec, setOpenSec] = usePersistedState<Record<string, boolean>>(`dq:${entityId}:openSec`, { Status: true });
  const toggleSec = (k: string) => setOpenSec((s) => ({ ...s, [k]: !s[k] }));
  const [q, setQ] = usePersistedState<string>(`dq:${entityId}:q`, "");
  const [busy, setBusy] = useState(false);
  const [gaps, setGaps] = useState<CompletenessFinding[]>([]);
  const [showGaps, setShowGaps] = useState(false);
  // Classification badges (trust/risk/family/scope/authority) are collapsed by default so the
  // card leads with version + decision + impact. She can pin them on; the choice persists.
  const [showClass, setShowClass] = usePersistedState<boolean>(`dq:${entityId}:showClass`, false);
  // UI.3 — popover rail (v2 only), controlled OR uncontrolled. Not persisted: a filter panel
  // that reopens itself on every mount is noise, and the filters it holds are persisted
  // already.
  const [railOpenLocal, setRailOpenLocal] = useState(false);
  const railControlled = railOpenProp !== undefined;
  const railOpen = railControlled ? !!railOpenProp : railOpenLocal;
  const setRailOpen = (v: boolean) => {
    if (!railControlled) setRailOpenLocal(v);
    onRailOpenChange?.(v);
  };

  const load = useCallback(async () => {
    try {
      const r = await sdh_rules(entityId, "All");
      setRules(r.rules || []); setCounts(r.counts as any || counts);
      onRulesLoaded?.(r.rules || []);   // UI.3 — keep a host's own roll-ups in step with this list
    }
    catch { onNote("Could not load rules"); }
    try { const g = await sdh_completeness(entityId); setGaps(g.findings || []); } catch { /* endpoint optional */ }
  }, [entityId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // ── Remember and restore the SME's scroll position ──────────────────────────
  const _scrollKey = `dq:${entityId}:scrollY`;
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        try { sessionStorage.setItem(_scrollKey, String(window.scrollY)); } catch { /* ignore */ }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [_scrollKey]);
  // Restore scroll once rules are present (i.e. after each load) — next frame so layout is ready.
  useEffect(() => {
    if (!rules.length) return;
    const y = Number(sessionStorage.getItem(_scrollKey) || 0);
    if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
  }, [rules.length, _scrollKey]);
  // Reload data when the parent signals a change (e.g. after a refine/decision save),
  // WITHOUT remounting — so the SME keeps her active bucket, filters, scroll and search.
  const _firstRefresh = React.useRef(true);
  useEffect(() => {
    if (_firstRefresh.current) { _firstRefresh.current = false; return; }
    load();
  }, [refreshSignal]);  // eslint-disable-line

  const view = (r: EnrichedRule) => previewed[r.rule_id] || r;
  const tableOf = (r: EnrichedRule) => String(r.owner_field || (r._raw || {}).owner_field_id || "::").split("::")[0].toUpperCase();
  // UI.3 — the FULL owner id, TABLE::COLUMN. `fieldOf` below returns only the column, which
  // would collide across tables (MARA::MATNR and MARC::MATNR both answer "MATNR").
  // UI.3.2 — who MADE this rule, and what set it off. D.1 stamps created_by/trigger on every
  // version and dq_v2_compat carries them into revisions[].author / .trigger, but nothing on
  // this screen showed them, so "FIND_DEPENDENCY generated it and G5 attached a control
  // afterwards" was indistinguishable from "the control produced it".
  const originOf = (r: EnrichedRule) => {
    const revs: any[] = ((r as any).revisions || []);
    const first = revs.find((v) => v?.is_base) || revs[0] || {};
    const cur = revs.length ? revs[revs.length - 1] : {};
    return {
      by: String(first.author || (r as any)._raw?.created_by || ""),
      trigger: typeof first.trigger === "string" ? first.trigger : (first.trigger?.kind || ""),
      lastBy: String(cur.author || ""),
      lastType: String(cur.change_type || ""),
      n: revs.length,
    };
  };

  const ownerFieldOf = (r: EnrichedRule) => String(r.owner_field || (r._raw || {}).owner_field_id || "").toUpperCase();
  // defined BEFORE its first use: the search filter (below) called it while it was still in the temporal dead
  // zone -> "Cannot access 'fieldOf' before initialization" as soon as the search box had text
  const polDoc = usePolicies(entityId);   // the policy each rule belongs to (rule cards)
  const fieldOf = (r: EnrichedRule) => String(r.owner_field || (r._raw || {}).owner_field_id || "::").split("::").pop() || "";
  const labelOf = (r: EnrichedRule) => (r as any).owner_label || fieldOf(r);

  // UI.3 — every "all rules" roll-up on this screen (gate segments, progress bar, coverage
  // count) is relative to the page's subject. In v1 there is no subject, so scopedRules IS
  // rules and nothing changes; in v2 the subject is the selected field, and a gate bar reading
  // "38 approved" above two visible rules would be a lie.
  const scopedRules = useMemo(
    () => (fieldFilter ? rules.filter((r) => ownerFieldOf(r) === fieldFilter.toUpperCase()) : rules),
    [rules, fieldFilter]); // eslint-disable-line

  const gapCount = useMemo(() => scopedRules.filter((r) => authOf(r) === "none").length, [scopedRules]);


  // one filter predicate; `ignore` lets each facet count reflect the OTHER active filters (faceted search)
  const passes = useCallback((r: EnrichedRule, ignore?: string) => {
    // UI.3 — owner-field scope, driven by the v2 field list. Deliberately NOT part of the
    // `ignore` facet system: it is the page's subject, not a facet the reviewer toggles, so
    // every facet count below must respect it.
    if (fieldFilter && ownerFieldOf(r) !== fieldFilter.toUpperCase()) return false;
    if (ignore !== "table" && field && tableOf(r) !== field) return false;
    if (ignore !== "family" && family && (FAM_LABEL[r.family || ""] || r.family) !== family) return false;
    if (ignore !== "scope" && scope && scopeOf(r) !== scope) return false;
    const rc: any = (r as any).review_classification || {};
    if (ignore !== "trust" && trust && rc.trust !== trust) return false;
    if (ignore !== "risk" && risk && rc.risk !== risk) return false;
    if (ignore !== "reason" && reason && rc.review_label !== reason) return false;
    if (ignore !== "gate" && gate !== "ALL" && gateOfRule(r) !== gate) return false;
    if (ignore !== "assess" && assessFilter && (r as any).sme_decision?.assessment !== assessFilter) return false;
    if (onlyViol && impactOf(view(r)).state !== "flags") return false;
    if (!showFlagged && flagsOf(r).some((f) => HIDDEN_FLAGS.has(f))) return false;
    if (onlyLookup && !(r as any).requires_reference_data
        && !String((r as any).reference_data_name || "").length
        && (r.family !== "code_domain" && r.family !== "lookup" && r.family !== "referential")) return false;
    if (onlyClientOk && !((r as any).policy_status === "backed_confirmed" || (r as any).rule_logic_authority === "client_confirmed")) return false;
    if (ignore !== "tag" && tagFilter) {
      const rv = tagFilter.lens === "dimension" ? dimensionOf(r) : ((r as any).tags?.[tagFilter.lens]);
      if (rv !== (tagFilter.state === "true" ? true : tagFilter.state)) return false;
    }
    if (q) { const s = `${r.rule_name} ${fieldOf(r)} ${r.business_logic} ${r.rule_id}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  }, [fieldFilter, field, family, scope, trust, risk, reason, gate, assessFilter, onlyViol, onlyLookup, onlyClientOk, tagFilter, q, previewed, showFlagged]); // eslint-disable-line

  const byTag = useCallback((lens: string) => {
    const t: Record<string, number> = {};
    for (const r of rules.filter((rr) => passes(rr, "tag"))) {
      const v = lens === "dimension" ? dimensionOf(r) : (r as any).tags?.[lens];
      if (v === undefined || v === null || v === false) continue;
      const key = v === true ? "true" : String(v);
      t[key] = (t[key] || 0) + 1;
    }
    return t;
  }, [rules, passes]);

  const byTable = useMemo(() => tally(rules.filter((r) => passes(r, "table")).map(tableOf)), [rules, passes]);
  const byFam = useMemo(() => tally(rules.filter((r) => passes(r, "family")).map((r) => FAM_LABEL[r.family || ""] || r.family || "—")), [rules, passes]);
  const byScope = useMemo(() => tally(rules.filter((r) => passes(r, "scope")).map(scopeOf)), [rules, passes]);
  const rcOf = (r: EnrichedRule) => (r as any).review_classification || {};
  const byTrust = useMemo(() => tally(rules.filter((r) => passes(r, "trust")).map((r) => rcOf(r).trust || "—")), [rules, passes]);
  const byRisk = useMemo(() => tally(rules.filter((r) => passes(r, "risk")).map((r) => rcOf(r).risk || "—")), [rules, passes]);
  const byReason = useMemo(() => tally(rules.filter((r) => passes(r, "reason")).map((r) => rcOf(r).review_label || "—")), [rules, passes]);
  // faceted counts for the top decision bar (respect all OTHER active filters)
  // Gate segment counts: ignore the gate filter itself so all segments stay visible.
  // Top status buckets are a fixed decision-progress overview — their counts reflect ALL rules
  // (optionally narrowed by the free-text search only), NOT the sidebar reason/table/tag filters.
  // Otherwise picking a reason like "Needs business confirmation" would wrongly shrink "All".
  const gatePool = useMemo(() => {
    // the tiles count what the list can show: flagged rules hidden from the list are not counted
    // (ALL 3 / NOT REVIEWED 2 while the list showed 2 / 1 - the third was a hidden "contradicted" rule)
    const base = showFlagged ? scopedRules : scopedRules.filter((r) => !flagsOf(r).some((f) => HIDDEN_FLAGS.has(f)));
    if (!q) return base;
    const needle = q.toLowerCase();
    return base.filter((r) => `${r.rule_name} ${fieldOf(r)} ${r.business_logic} ${r.rule_id}`.toLowerCase().includes(needle));
  }, [scopedRules, q, showFlagged]); // eslint-disable-line
  const gateCount = (g: GateKey) => g === "ALL" ? gatePool.length : gatePool.filter((r) => gateOfRule(r) === g).length;
  // Reason counts: RESPECT the selected gate (ignore only the assessment filter), so picking "Approved"
  // shows only Approved reasons — the sidebar section is genuinely dynamic to the current gate.
  const assessPool = useMemo(() => rules.filter((r) => passes(r, "assess")), [rules, passes]);
  const assessCounts = useMemo(() => {
    const t: Record<string, number> = {};
    for (const r of assessPool) {
      const a = (r as any).sme_decision?.assessment; if (a) t[a] = (t[a] || 0) + 1;
    }
    return t;
  }, [assessPool]);

  // SME action queue: a bucket shows its rules across ALL fields (only the search still applies)
  // a status from the queue applies to the SELECTED field (the field list already shows only fields that have it)
  const saCatNow = SA_STORE.cat;                       // the set-aside category picked in the queue (if any)
  const shown = useMemo(() => lifeFilter
    ? scopedRules.filter((r) => matchBucket(r, lifeFilter)
        && (lifeFilter !== "hidden" || !saCatNow || saOf(ruleKey(r))?.category === saCatNow) && (!q || `${r.rule_name} ${fieldOf(r)} ${r.business_logic} ${r.rule_id}`.toLowerCase().includes(q.toLowerCase())))
    : rules.filter((r) => passes(r)), [rules, scopedRules, passes, lifeFilter, q, saCatNow]); // eslint-disable-line
  const grouped = useMemo(() => groupBy(shown, fieldOf), [shown]);

  // UI.3 — how many facets are narrowing the list right now. `fieldFilter` is excluded on
  // purpose: it is the page's subject, not something the reviewer set here, and a host that
  // shows "Filters · 1" the moment a field is clicked teaches her to distrust the badge.
  const activeFilters = useMemo(() => [
    gate !== "ALL", !!assessFilter, !!field, !!family, !!scope, !!trust, !!risk, !!reason,
    onlyViol, onlyLookup, onlyClientOk, !!tagFilter, !!q,
  ].filter(Boolean).length,
    [gate, assessFilter, field, family, scope, trust, risk, reason, onlyViol, onlyLookup, onlyClientOk, tagFilter, q]);
  useEffect(() => { onActiveFilters?.(activeFilters); }, [activeFilters]); // eslint-disable-line

  const clearFilters = () => {
    setGate("ALL"); setAssessFilter(null); setField(null); setFamily(null); setScope(null);
    setTrust(null); setRisk(null); setReason(null); setOnlyViol(false); setOnlyLookup(false);
    setOnlyClientOk(false); setTagFilter(null); setQ("");
  };

  // UI.3 — the rail is rendered from ONE piece of markup in both modes. The children below are
  // byte-for-byte v1's; only the container changes, so a future facet added to the rail appears
  // on both screens without anyone remembering to do it twice.
  const railWrap = (children: React.ReactNode) => railMode === "popover"
    ? (railOpen ? (
        <div className="dqc-pop-scrim" onClick={() => setRailOpen(false)}>
          <aside className="dqc-rail dqc-rail-pop" onClick={(e) => e.stopPropagation()}>
            <div className="dqc-rail-poph">
              <b>Filters</b>
              {activeFilters > 0 && <button className="clr" onClick={clearFilters}>Clear all</button>}
              <button className="x" title="Close" onClick={() => setRailOpen(false)}>×</button>
            </div>
            <div className="dqc-rail-popb">{children}</div>
          </aside>
        </div>
      ) : null)
    : (
      <aside className="dqc-rail" style={{ overflowY: "auto", maxHeight: "calc(100vh - 80px)", position: "sticky", top: 0 }}>
        {children}
      </aside>
    );

  const commitDecision = (id: string, assessment: string, comment: string) =>
    setDecisions((d) => ({ ...d, [id]: { assessment, comment } }));
  const bulkApply = (assessment: string, comment: string) => setDecisions((d) => {
    const next = { ...d };
    for (const r of shown) next[r.rule_id] = { assessment, comment };
    return next;
  });
  const clearAll = () => setDecisions({});
  const saveAll = async () => {
    const payload = Object.entries(decisions).map(([id, dr]) => {
      const def = ASSESSMENT_BY_VALUE[dr.assessment];
      return {
        constraint_id: id, decision_type: "rule_review",
        choice: def?.choice || "reject_rule",
        assessment: dr.assessment, decision_gate: def?.gate, comment: dr.comment || "",
        sme_id: smeId, sme_name: user?.full_name || user?.email || undefined,
      };
    });
    if (!payload.length) return;
    setBusy(true);
    try { await sdh_applyDecisions(entityId, appNm, payload); onNote(`Saved ${payload.length} decision(s).`); setDecisions({}); await load(); }
    catch { onNote("Save failed"); } finally { setBusy(false); }
  };
  const runPreview = async (r: EnrichedRule) => {
    onNote(`Measuring ${labelOf(r)}…`);
    try {
      const res: any = await sdh_preview(entityId, appNm, [r.rule_id], "all");
      const { hit, compiled, note } = readPreview(res, r.rule_id);
      if (hit) {
        setPreviewed((p) => ({ ...p, [r.rule_id]: { ...r,
          impact_status: hit.status || ((hit.violating_rows ?? hit.violations ?? 0) > 0 ? "FAIL" : "PASS"),
          impact_violations: hit.violating_rows ?? hit.violations ?? 0,
          impact_applicable_rows: hit.applicable_rows ?? hit.total_rows ?? null,
          impact_pass_rate: hit.pass_rate ?? null } }));
      } else {
        // compiler has no executor for this family (e.g. formula / unit) — say so instead of failing
        setPreviewed((p) => ({ ...p, [r.rule_id]: { ...r, impact_status: "UNSUPPORTED" } }));
        onNote(compiled === 0 ? "This rule type can’t be auto-measured yet." : (note || "No measurable rows for this rule."));
      }
    } catch { onNote("Preview failed"); }
  };

  const pendingN = Object.keys(decisions).length;
  const pendingGates = Object.values(decisions).reduce((m, d) => {
    const g = gateOf(d.assessment) || "REJECTED"; m[g] = (m[g] || 0) + 1; return m;
  }, {} as Record<string, number>);

  return (
    <div className={"dqc-grid" + (railMode === "popover" ? " norail" : "")}>
      {/* counts */}
      <div className="dqc-full">
        {/* Two-level decision filter: gate segments (level 1) → assessment chips (level 2).
            Replaces the old STATUS sidebar section and the static counts tiles. */}
        <div className={"dqc-gatebar" + (hideGates ? " hide" : "")}>
          {GATE_META.map((g) => {
            const n = gateCount(g.key);
            return (
              <button key={g.key} className={"dqc-gseg" + (gate === g.key ? " on" : "")}
                style={{ ["--t" as any]: g.tone }}
                onClick={() => { setGate(g.key); setAssessFilter(null); }}>
                <span className="n">{n}</span><span className="l">{g.label}</span>
              </button>
            );
          })}
        </div>
        <div className="dqc-prog" title="Decision progress across all rules">
          {(() => {
            const tot = rules.length || 1;
            const w = (g: GateKey) => `${(rules.filter((r) => gateOfRule(r) === g).length / tot) * 100}%`;
            return <>
              <i className="pa" style={{ width: w("APPROVED") }} />
              <i className="pc" style={{ width: w("PRE_APPROVED") }} />
              <i className="pp" style={{ width: w("PENDING") }} />
              <i className="pr" style={{ width: w("REJECTED") }} />
            </>;
          })()}
        </div>
      </div>

      {/* rail — inline in v1, popover in v2; same children either way */}
      {railWrap(<>
        {(() => {
          const TAG_LABELS: Record<string, string> = {
            measured_clean: "Passed (clean)", measured_flags: "Flags data",
            not_measured_needs_values: "Needs governed values", not_measured_config: "Needs config",
            not_measured_uncompilable: "Engine can't compile yet",
            not_measured_no_rows: "No matching rows", not_measured_error: "Engine error",
            bound_governed: "Governed set bound", values_pending: "Values pending", not_a_valueset: "—",
            not_challenged: "Not reviewed", validated: "Reviewer validated", refined: "Refined (applied)",
            unresolved_objection: "Unresolved objection", recommend_drop: "Reviewer: drop",
            values_client_governed: "Values client-governed", column_client_governed: "Column governed · values pending",
            client_rule_confirmed: "Client-rule confirmed", measured_observed: "Observed evidence", none: "—",
            true: "Likely rule defect",
          };
          const Sec = ({ title, k, children, always }: any) => (
            <div className="dqc-sec">
              {always ? <h4>{title}</h4> :
                <h4 className="dqc-sec-h" onClick={() => toggleSec(k)}><span>{title}</span><span className="chev">{openSec[k] ? "▼" : "▶"}</span></h4>}
              {(always || openSec[k]) && <div>{children}</div>}
            </div>
          );
          const tagSec = (title: string, k: string, lens: string, order?: string[], help?: string) => {
            const counts = byTag(lens); const keys = (order || Object.keys(counts)).filter((x) => counts[x]);
            if (!keys.length) return null;
            const head = help ? <span>{title}<HelpDot topic={help} /></span> : title;
            return <Sec title={head} k={k}>
              {keys.map((st) => (
                <Opt key={st} label={TAG_LABELS[st] || st} count={counts[st]}
                  on={tagFilter?.lens === lens && tagFilter?.state === st}
                  onClick={() => setTagFilter(tagFilter?.lens === lens && tagFilter?.state === st ? null : { lens, state: st })} />
              ))}
            </Sec>;
          };
          return <>
            {/* Decision status now lives in the top decision bar (two-level gate → reason). */}

            {/* DECISION REASON — dynamic: rendered from the backend taxonomy, so new assessments
                appear automatically with no UI change. Grouped by gate, counts respect other filters. */}
            {Object.keys(assessCounts).length > 0 && (
              <Sec title={<span>Decision reason<HelpDot topic="reviewer" /></span>} k="dreason">
                {(["APPROVED", "PENDING", "REJECTED"] as const).map((g) => {
                  const items = assessDefs.filter((a) => a.gate === g && assessCounts[a.value]);
                  if (!items.length) return null;
                  return (
                    <div key={g} className="dqc-rgrp">
                      <div className="dqc-rgrp-h" style={{ color: GATE_TONE[g] }}>{g.toLowerCase()}</div>
                      {items.map((a) => (
                        <Opt key={a.value} label={a.label.replace(/^[^—]+—\s*/, "")} count={assessCounts[a.value]}
                          on={assessFilter === a.value}
                          onClick={() => setAssessFilter(assessFilter === a.value ? null : a.value)} />
                      ))}
                    </div>
                  );
                })}
              </Sec>
            )}

            {/* DQ DIMENSION — the quality question each rule answers */}
            {tagSec("Quality dimension", "dim", "dimension",
              ["Completeness", "Validity", "Conformity", "Consistency", "Integrity", "Uniqueness"], "dimension")}

            {/* MEASUREMENT — did it run on your data, and what did it find */}
            {tagSec("Measurement", "meas", "execution_state",
              ["measured_flags", "measured_clean", "not_measured_needs_values", "not_measured_uncompilable", "not_measured_no_rows", "not_measured_error"], "measurement")}

            {/* REVIEWER — challenge / objection / refine (correct, split) */}
            {tagSec("Reviewer", "rev", "challenge_state",
              ["unresolved_objection", "recommend_drop", "refined", "validated", "not_challenged"], "reviewer")}

            {/* LIKELY DEFECTS — over-broad flaggers */}
            {(() => { const c = byTag("suspect_overbroad"); return c["true"] ?
              <Sec title={<span>Attention<HelpDot topic="attention" /></span>} k="att"><Opt label="⚠ Likely rule defect" count={c["true"]}
                on={tagFilter?.lens === "suspect_overbroad" && tagFilter?.state === "true"}
                onClick={() => setTagFilter(tagFilter?.lens === "suspect_overbroad" ? null : { lens: "suspect_overbroad", state: "true" })} /></Sec> : null; })()}

            {/* AUTHORITY — where the rule's trust comes from */}
            {tagSec("Authority", "auth", "authority_state",
              ["client_rule_confirmed", "values_client_governed", "column_client_governed", "measured_observed"], "authority")}

            {/* REFERENCE DATA — governed value-set state */}
            {tagSec("Reference data", "ref", "reference_state", ["bound_governed", "values_pending"], "measurement")}

            {gate !== "APPROVED" && <>
              <Sec title="Prioritise · Trust" k="trust">
                {["Confirmed", "Supported", "Tentative", "Unverified"].filter((t) => byTrust[t]).map((t) => (
                  <Opt key={t} label={t} count={byTrust[t]} on={trust === t} onClick={() => setTrust(trust === t ? null : t)} />
                ))}
              </Sec>
              <Sec title="Prioritise · Risk" k="risk">
                {["High", "Moderate", "Low"].filter((t) => byRisk[t]).map((t) => (
                  <Opt key={t} label={t + " risk"} count={byRisk[t]} on={risk === t} onClick={() => setRisk(risk === t ? null : t)} />
                ))}
              </Sec>
              <Sec title="Prioritise · What to do" k="todo">
                {Object.keys(byReason).sort((a, b) => byReason[b] - byReason[a]).map((t) => (
                  <Opt key={t} label={t} count={byReason[t]} on={reason === t} onClick={() => setReason(reason === t ? null : t)} />
                ))}
              </Sec>
            </>}
            <Sec title="Table" k="table">
              {Object.keys(byTable).sort().map((t) => (
                <Opt key={t} label={t} count={byTable[t]} on={field === t} onClick={() => setField(field === t ? null : t)} />
              ))}
            </Sec>
            <Sec title="Rule type" k="ruletype">
              {Object.keys(byFam).sort().map((t) => (
                <Opt key={t} label={t} count={byFam[t]} on={family === t} onClick={() => setFamily(family === t ? null : t)} />
              ))}
            </Sec>
            <Sec title="Scope" k="scope">
              {(["cross-table", "cross-field", "single"] as Scope[]).map((s) => (
                <Opt key={s} label={s} count={byScope[s] || 0} on={scope === s} onClick={() => setScope(scope === s ? null : s)} />
              ))}
            </Sec>
          </>;
        })()}
        <div className="dqc-gap">
          <b>⚑ {gapCount} coverage items</b>
          <p>Novel rules with no control in your library yet — candidates to adopt as policy.</p>
        </div>
        {gaps.length > 0 && (
          <div className="dqc-gap datagap" onClick={() => setShowGaps(true)}>
            <b>▤ {gaps.length} data gaps</b>
            <p>Governed fields that are ~100% empty. Not rules — click to review the report.</p>
          </div>
        )}
      </>)}

      {/* list */}
      <main className="dqc-list">
        <div className="dqc-toolbar">
          <div className="dqc-search">🔎<input value={q} placeholder="Search rules, fields, logic…" onChange={(e) => setQ(e.target.value)} /></div>
          <button className={"dqc-toggle" + (onlyViol ? " on" : "")} onClick={() => setOnlyViol((v) => !v)}>⚠ Only rules that flag data</button>
          {(() => {
            const n = scopedRules.filter((r) => flagsOf(r).some((f) => HIDDEN_FLAGS.has(f))).length;
            return n ? (
              <button className={"dqc-toggle" + (showFlagged ? " on" : "")} onClick={() => setShowFlagged((v) => !v)}
                title="Rules the sample shows to be vacuous, contradicted, untested or rejected - hidden by default">
                ⚑ {showFlagged ? "Hide" : "Show"} flagged ({n})
              </button>
            ) : null;
          })()}
          <button className={"dqc-toggle" + (onlyLookup ? " on" : "")} onClick={() => setOnlyLookup((v) => !v)} title="Rules that validate a value against a governed lookup / reference-data list">▤ Reference-data lookups</button>
          <button className={"dqc-toggle" + (onlyClientOk ? " on" : "")} onClick={() => setOnlyClientOk((v) => !v)} title="Rules confirmed by a client-authored data rule (highest trust)">✓ Client-confirmed</button>
          {/* UI.3 — the cockpit only draws its own Filters trigger when the host has not taken
              control of the popover. A host that passes railOpen puts the trigger wherever it
              wants and this renders nothing. */}
          {railMode === "popover" && !railControlled && (
            <button className={"dqc-toggle" + (activeFilters ? " on" : "")} onClick={() => setRailOpen(true)}>
              ⚙ Filters{activeFilters ? ` · ${activeFilters}` : ""}
            </button>
          )}
          {toolbarExtra}
        </div>
        <div className="dqc-resultbar">
          {lifeFilter && <span className="rv-filter">Showing <b>{lifeFilter === "proposal" ? "Proposals" : lifeFilter === "done" ? "Done" : LIFE[lifeFilter as keyof typeof LIFE]?.label}</b>{lifeFilter === "hidden" && saCatNow && SA_STORE.summary?.[saCatNow] ? <b> · {SA_STORE.summary[saCatNow].label}</b> : null} for this field
            <button onClick={() => onClearLife?.()}>show all</button></span>}
          <span className="cnt">Showing <b>{shown.length}</b> of {rules.length} rules</span>
          <button className={"dqc-classtoggle" + (showClass ? " on" : "")} onClick={() => setShowClass(!showClass)}
            title="Show or hide the agent's trust / risk / family / authority badges on each card">
            <i className={"ti " + (showClass ? "ti-eye" : "ti-eye-off")} aria-hidden="true" style={{ fontSize: 13, verticalAlign: -2, marginRight: 4 }} />
            {showClass ? "Hide classification" : "Show classification"}
          </button>
          {shown.length > 1 && gate !== "APPROVED" && (
            <span className="dqc-bulk">
              <button className="dqc-decide" onClick={() => setBulkPop(true)}>Apply to all {shown.length} shown…<span className="cv">▾</span></button>
            </span>
          )}
          {(() => {
            const chips = [
              gate !== "ALL" ? { k: "gate", label: GATE_META.find(g => g.key === gate)?.label || gate, clear: () => { setGate("ALL"); setAssessFilter(null); } } : null,
              assessFilter ? { k: "assess", label: ASSESSMENT_BY_VALUE[assessFilter]?.label || assessFilter, clear: () => setAssessFilter(null) } : null,
              field ? { k: "table", label: `Table: ${field}`, clear: () => setField(null) } : null,
              family ? { k: "family", label: `Type: ${family}`, clear: () => setFamily(null) } : null,
              scope ? { k: "scope", label: `Scope: ${scope}`, clear: () => setScope(null) } : null,
              trust ? { k: "trust", label: `Trust: ${trust}`, clear: () => setTrust(null) } : null,
              risk ? { k: "risk", label: `Risk: ${risk}`, clear: () => setRisk(null) } : null,
              reason ? { k: "reason", label: `To do: ${reason}`, clear: () => setReason(null) } : null,
              onlyViol ? { k: "viol", label: "flags data", clear: () => setOnlyViol(false) } : null,
              onlyLookup ? { k: "lookup", label: "reference-data lookups", clear: () => setOnlyLookup(false) } : null,
              onlyClientOk ? { k: "clientok", label: "client-confirmed", clear: () => setOnlyClientOk(false) } : null,
              tagFilter ? { k: "tag", label: `${tagFilter.lens.replace(/_/g, " ").replace(" state", "")}: ${tagFilter.state}`, clear: () => setTagFilter(null) } : null,
              q ? { k: "q", label: `“${q}”`, clear: () => setQ("") } : null,
            ].filter(Boolean) as any[];
            if (!chips.length) return null;
            return <>
              {chips.map((c: any) => (
                <span key={c.k} className="dqc-fpill">{c.label}{" "}
                  <span className="x" role="button" title="Remove filter"
                    style={{ cursor: "pointer" }} onClick={c.clear}>×</span>
                </span>
              ))}
              {chips.length > 1 && (
                <button className="dqc-fclear" style={{ cursor: "pointer", marginLeft: 6 }}
                  onClick={clearFilters}>
                  Clear all
                </button>
              )}
            </>;
          })()}
        </div>

        {shown.length === 0 ? (
          <div className="dqc-empty">No rules match these filters. Clear a filter to see more.</div>
        ) : Object.keys(grouped).sort().map((f) => (
          <div key={f}>
            <div className="dqc-fhead">
              <b className="fh-label">{grouped[f][0] && labelOf(grouped[f][0])}</b>
              <span className="fh-tech">{grouped[f][0] && tableOf(grouped[f][0])}.{f}</span>
              <span className="fh-n">{grouped[f].length} rule{grouped[f].length !== 1 ? "s" : ""}</span>
            </div>
            {grouped[f].map((r0) => {
              const r = view(r0); const im = impactOf(r); const au = authOf(r); const sc = scopeOf(r);
              const draft = decisions[r.rule_id]; const ch = gateOf(draft?.assessment); const raw: any = r._raw || {};
              // the card's status line - the same words as the queue and the rule details (the BC line
              // "novel - not in control library" was the same on every rule)
              const lf = lifeOf(r); const flg = flagsOf(r).find((x) => HIDDEN_FLAGS.has(x));
              const policy = <span className="dqc-life" style={{ color: LIFE[lf].tone }}>
                <b>{LIFE[lf].label}</b>{lf === "hidden" && saOf(ruleKey(r)) ? ` — ${saOf(ruleKey(r)).label}`
                  : lf === "hidden" && flg ? ` — ${FLAG_LABEL[flg] || flg}`
                  : lf === "preapproved" ? " — on evidence, confirm or refine" : lf === "review" ? " — waiting for your decision" : ""}
                {pendingProposal(r) ? <span className="dqc-prop"> · proposal v{pendingProposal(r)!.version} waiting</span> : null}
                {policyOfRule(polDoc, r.rule_id) ? <span className="dqc-muted"> · policy: {policyOfRule(polDoc, r.rule_id)!.policy.name}</span> : null}</span>;
              void au; void AUTH_LABEL;
              return (
                <div key={r.rule_id} className={"dqc-card" + (ch ? " done-" + ch.toLowerCase() : "")}>
                  <div className="dqc-rname">
                    {r.rule_name || r.rule_id}
                    <span className="dqc-rid" title="Rule id">{r.rule_id}</span>
                    {(() => {   // version badge — always visible when the rule has moved past v1
                      const v = Number((r as any).version || 1);
                      return v > 1 ? <span className="dqc-vbadge" title={`Currently on version ${v}`}>v{v}</span> : null;
                    })()}
                    {draft && <span className="dqc-donetag" style={{ background: GATE_TONE[ASSESSMENT_BY_VALUE[draft.assessment]?.gate as keyof typeof GATE_TONE] || "#888" }}>{ASSESSMENT_BY_VALUE[draft.assessment]?.label || draft.assessment}</span>}
                    <DecisionBadge r={r} />
                    {saOf(ruleKey(r))
                      ? <span className={"dqc-satag " + saOf(ruleKey(r)).category}
                          title={`${saOf(ruleKey(r)).action}  ·  flags: ${flagsOf(r).join(", ")}`}>
                          {saOf(ruleKey(r)).label}</span>
                      : <FlagBadges r={r} />}
                    <ProposalBadge r={r} />
                    {(() => {   // a revised rule stays in Review under its decision — say so here
                      const rf: any = (r as any).refinement;
                      if (!rf?.action || rf.action === "KEEP") return null;
                      return <span className="dqc-revised" title={rf.explanation || "Revised by the agent"}>
                        ✎ agent revised{(r as any).version ? ` · v${(r as any).version}` : ""}</span>;
                    })()}
                  </div>
                  {/* F5 — the LLM now writes a short rule_title AND a fuller business_summary.
                      Show the summary as a subtitle when it adds something beyond the title, so
                      the SME gets the one-line assertion without opening details. */}
                  {(() => {
                    const title = String(r.rule_name || "").trim();
                    const summary = String((r as any).business_summary || "").trim();
                    if (summary && summary.toLowerCase() !== title.toLowerCase()) {
                      return <div className="dqc-subtitle">{summary}</div>;
                    }
                    return null;
                  })()}
                  {/* UI.3.2 — authorship is NOT behind the "show classification" toggle. Which
                      producer made a rule is a first-order review fact, not a detail. */}
                  {(() => {
                    const o = originOf(r);
                    if (!o.by) return null;
                    const lbl = MAKER_LABEL[o.by] || o.by.toLowerCase().replace(/_/g, " ");
                    const cls = o.by.indexOf("FIND_DEPENDENCY") >= 0 ? "llm"
                      : o.by.indexOf("EK_INGEST") >= 0 ? "ek"
                      : o.by.indexOf("SDH") >= 0 ? "sdh" : "other";
                    return (
                      <div className="dqc-made">
                        <span className={"m " + cls} title={`created_by=${o.by}${o.trigger ? ` · trigger=${o.trigger}` : ""}`}>
                          {(() => { const et = String((r as any).evidence_type || (r as any)._raw?.evidence_type || "");
                            const O: Record<string, string> = { profiling_evidence: "found in your data", sap_standard: "SAP standard knowledge",
                              domain_knowledge: "domain knowledge", client_documented: "your documentation" };
                            return O[et] ? `origin: ${O[et]}` : `made by ${lbl}`; })()}
                        </span>
                        {o.n > 1 && o.lastBy && o.lastBy !== o.by && (
                          <span className="m other" title={`last change by ${o.lastBy} (${o.lastType})`}>
                            last changed by {MAKER_LABEL[o.lastBy] || o.lastBy.toLowerCase().replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {showClass && <div className="dqc-badges">
                    {(() => { const rc = (r as any).review_classification || {}; return <>
                      <span className={"b trust t-" + (rc.trust || "").toLowerCase()} title={rc.explain?.trust}>{rc.trust_label || "—"}</span>
                      <span className={"b risk k-" + (rc.risk || "").toLowerCase()} title={rc.explain?.risk}>{rc.risk_label} risk</span>
                      {rc.review_reason && <span className="b reason" title={rc.explain?.reason}>{rc.review_label}</span>}
                      {rc.recommend_quick_approve && <span className="b quick" title="Trust + risk make this safe to clear quickly — guidance only">★ quick-approve</span>}
                    </>; })()}
                    <span className="b fam">{FAM_LABEL[r.family || ""] || r.family}</span>
                    {(() => { const dv = dimensionOf(r); return dv ? <span className={"b dim dim-" + dv.toLowerCase()} title={`Data-quality dimension: ${dv}`}>{dv}</span> : null; })()}
                    {sc !== "single" && <span className={"b " + (sc === "cross-table" ? "xt" : "xf")}>{sc}</span>}
                    {(() => {
                      const t = (r as any).tags || {};
                      const cs = t.challenge_state;
                      if (cs === "unresolved_objection") return <span className="b rev-obj" title={(r as any).tag_notes?.challenge || "Reviewer raised an unresolved issue — needs your judgment"}>⚠ needs judgment</span>;
                      if (cs === "recommend_drop") return <span className="b rev-obj" title={(r as any).tag_notes?.challenge}>⚠ reviewer: drop</span>;
                      if (cs === "refined") return <span className="b rev-refined" title={(r as any).tag_notes?.challenge || "Reviewer refined this rule; change applied"}>✎ refined</span>;
                      if (cs === "validated") return <span className="b rev-ok" title="Reviewer checked and validated — no changes">✓ reviewed</span>;
                      return null;
                    })()}
                    {(r as any).tags?.suspect_overbroad && <span className="b suspect" title={(r as any).tag_notes?.suspect || "Flags an implausibly high share of rows — likely a rule defect, not a data issue"}>⚠ likely rule defect</span>}
                    {(() => {
                      const as_ = (r as any).tags?.authority_state;
                      if (as_ === "client_rule_confirmed") return <span className="b clientok" title={(r as any).tag_notes?.authority}>✓ client-confirmed</span>;
                      if (as_ === "values_client_governed") return <span className="b clientval" title={(r as any).tag_notes?.authority}>✓ client-governed values</span>;
                      if (as_ === "column_client_governed") return <span className="b clientpend" title={(r as any).tag_notes?.authority}>client-governed · values pending</span>;
                      return null;
                    })()}
                  </div>}
                  {((r as any).plain || raw.business_reason || r.description) &&
                    <div className="dqc-why">{(r as any).plain || raw.business_reason || r.description}</div>}
                  {(() => {   // the reviewer's own words, where she can see them while triaging
                    const d: any = (r as any).sme_decision || {};
                    if (!d.comment) return null;
                    const g = d.decision_gate as keyof typeof GATE_TONE | undefined;
                    return <div className="dqc-smecmt" style={g ? { ["--t" as any]: GATE_TONE[g] } : undefined}>
                      <b>{ASSESSMENT_BY_VALUE[d.assessment]?.label || d.decision_gate}</b>
                      <span className="w">{d.sme_name || d.sme_id || "SME"}{d.decided_at ? ` · ${String(d.decided_at).slice(0,10)}` : ""}</span>
                      <div className="c">“{d.comment}”</div>
                    </div>;
                  })()}
                  {(r as any).review_note &&
                    <div className="dqc-note">{(r as any).review_note}</div>}
                  <div className="dqc-meta"><span>{policy}</span>
                    <span>field <b>{labelOf(r)}</b> <span className="phys">{tableOf(r)}.{fieldOf(r)}</span></span></div>

                  {/* impact — signature */}
                  {im.state === "unsupported" ? (
                    <div className="dqc-impact un">
                      <span className="lab">Not auto-measurable</span>
                      <span className="untext">This rule type isn’t executable in the engine yet — approve on the evidence, and it won’t block enforcement of the others.</span>
                    </div>
                  ) : im.state === "not_applicable" ? (
                    <div className="dqc-impact un">
                      <span className="lab">No rows to check</span>
                      <span className="untext">{im.note || "This rule ran, but its condition matches 0 records in the current data — there is nothing to flag here."} (Risk below is a structural estimate, not a measurement.)</span>
                    </div>
                  ) : im.state === "unmeasured" ? (
                    <div className="dqc-impact un">
                      <span className="lab">Not yet measured</span>
                      <span className="untext">See how many records this would flag before you approve.</span>
                      <button className="dqc-mini" onClick={() => runPreview(r0)}>Preview impact</button>
                    </div>
                  ) : !measurementOf(r).ok ? (
                    /* Numbers are only shown when they belong to THIS version of the rule. */
                    <div className="dqc-impact un"><NotMeasured m={measurementOf(r)} /></div>
                  ) : (
                    <div className="dqc-impact">
                      <div className="lab">
                        <span>Impact on live data</span>
                        {im.state === "flags" ? <span className="r">flags {fmt(im.viol)} of {fmt(im.appl)}</span> : <span className="g">clean</span>}
                      </div>
                      <div className="dqc-ibar">
                        <i className="pass" style={{ width: `${(im.pass ?? 1) * 100}%` }} />
                        <i className="flag" style={{ width: `${(1 - (im.pass ?? 1)) * 100}%` }} />
                      </div>
                      <div className="dqc-iline">
                        {im.state === "flags"
                          ? <><span className="r">{fmt(im.viol)}</span> records violate · <span className="g">{pctf(im.pass)}</span> pass — approving raises this as a finding</>
                          : <><span className="g">{pctf(im.pass)}</span> of {fmt(im.appl)} records pass — safe to enforce</>}
                      </div>
                    </div>
                  )}

                  <div className="dqc-acts">
                    {lifecycleOf(r.approval_status) === "Rejected" ? (
                      <>
                        <span className="dqc-rejreason">✕ Rejected{(r as any).rejected_by ? ` by ${(r as any).rejected_by}` : ""}{(r as any).rejection_reason ? ` — ${(r as any).rejection_reason}` : ""}</span>
                        <DecideButton draft={decisions[r.rule_id]} onOpen={() => setPopFor(r.rule_id)} />
                        <button className="dqc-btn info" onClick={() => onOpen(r.rule_id)}>Details & evidence</button>
                      </>
                    ) : (
                      <>
                        <DecideButton draft={decisions[r.rule_id]} onOpen={() => setPopFor(r.rule_id)} />
                        <button className="dqc-btn info" onClick={() => onOpen(r.rule_id)}>Details & evidence</button>
                        <span className="dqc-expand" onClick={(e) => togglePre(e)}>view logic ⌄</span>
                      </>
                    )}
                  </div>
                  <div className="dqc-detail">
                    <div className="k">What the rule checks</div>
                    <RuleLogicView rule={r} compact
                      fallback={humanizeLogic(r.business_logic, r) || (r as any).expected_outcome || "—"} />
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>Open <b>Details &amp; evidence</b> for engine-generated SQL (MS-SQL/Oracle/Postgres) and the impact preview.</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </main>

      {showGaps && <DataGapsPanel gaps={gaps} onClose={() => setShowGaps(false)} />}

      {popFor && (() => {
        const r = rules.find((x) => x.rule_id === popFor);
        return <DecisionPopover defs={assessDefs} anchorLabel={r?.rule_name || popFor}
          value={decisions[popFor]?.assessment || (r as any)?.sme_decision?.assessment}
          comment={decisions[popFor]?.comment}
          onCancel={() => setPopFor(null)}
          onCommit={(a, c) => { commitDecision(popFor, a, c); setPopFor(null); }} />;
      })()}
      {bulkPop && (
        <DecisionPopover defs={assessDefs} anchorLabel={`Apply to all ${shown.length} shown rules`}
          onCancel={() => setBulkPop(false)}
          onCommit={(a, c) => { bulkApply(a, c); setBulkPop(false); }} />
      )}

      {/* decision tray */}
      {pendingN > 0 && (
        <div className="dqc-tray">
          <span className="t">{pendingN} pending — {pendingGates.APPROVED || 0} approve · {pendingGates.PENDING || 0} pending · {pendingGates.REJECTED || 0} reject</span>
          <span style={{ flex: 1 }} />
          <button className="undo" onClick={clearAll}>Clear</button>
          <button className="save" disabled={busy} onClick={saveAll}>{busy ? "Saving…" : "Save decisions"}</button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── small bits ─────────────────────────── */
function Opt({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return <div className={"dqc-opt" + (on ? " on" : "")} onClick={onClick}><span>{label}</span><span className="ct">{count}</span></div>;
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
function tally(arr: string[]) { const m: Record<string, number> = {}; arr.forEach((k) => (m[k] = (m[k] || 0) + 1)); return m; }
function groupBy<T>(arr: T[], key: (t: T) => string) { const m: Record<string, T[]> = {}; arr.forEach((x) => { const k = key(x); (m[k] = m[k] || []).push(x); }); return m; }
function togglePre(e: React.MouseEvent) {
  const card = (e.currentTarget as HTMLElement).closest(".dqc-card"); const d = card?.querySelector(".dqc-detail");
  if (d) { d.classList.toggle("open"); (e.currentTarget as HTMLElement).textContent = d.classList.contains("open") ? "hide logic ⌃" : "view logic ⌄"; }
}
function readPreview(res: any, ruleId: string): { hit: any; compiled?: number; note?: string } {
  const pv = res?.preview || res || {};
  const list: any[] = pv.per_rule || res?.rule_results || res?.results || [];
  const hit = list.find((x) => (x.control_id || x.constraint_id || x.rule_id) === ruleId) || (list.length === 1 ? list[0] : null);
  return { hit, compiled: pv.compiled, note: pv.note };
}

/* ─────────────────────────── data profile ─────────────────────────── */
function ProfileTab({ entityId }: { entityId: string }) {
  const [tables, setTables] = useState<ProfileTable[]>([]);
  const [table, setTable] = useState<string>("");
  const [cols, setCols] = useState<ProfileColumn[]>([]);
  const [col, setCol] = useState<ProfileColumnDetail | null>(null);
  useEffect(() => { (async () => { try { const r = await sdh_dataProfile(entityId); setTables(r.tables || []); if (r.tables?.[0]) setTable(r.tables[0].table); } catch {} })(); }, [entityId]);
  useEffect(() => { if (!table) return; (async () => { try { const r = await sdh_dataProfile(entityId, table); setCols(r.columns || []); setCol(null); } catch {} })(); }, [entityId, table]);
  const openCol = async (c: ProfileColumn) => { try { setCol(await sdh_dataProfileColumn(entityId, c.table, c.column)); } catch {} };
  return (
    <div>
      <div className="dqc-pillrow">
        {tables.map((t) => <button key={t.table} className={"dqc-pill" + (table === t.table ? " on" : "")} onClick={() => setTable(t.table)}>{t.table} ({t.columns})</button>)}
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <div className="dqc-tablewrap" style={{ flex: 2 }}>
          <table className="dqc-table">
            <thead><tr><th>Column</th><th>Type</th><th>Distinct</th><th>Null %</th><th>Mandatory</th></tr></thead>
            <tbody>{cols.map((c) => (
              <tr key={c.column} className="dqc-trow" onClick={() => openCol(c)}>
                <td><b>{c.column}</b></td><td>{c.data_type}</td><td>{fmt(c.distinct)}</td><td>{c.null_pct}%</td><td>{c.mandatory ? "✓" : ""}</td>
              </tr>))}</tbody>
          </table>
        </div>
        <div style={{ flex: 1 }}>
          {col ? (
            <div className="dqc-panel">
              <b>{col.column}</b>
              <div className="dqc-muted">{col.data_type} · {fmt(col.records)} records · {col.null_pct}% null · {fmt(col.distinct)} distinct</div>
              <h5>Top values</h5>
              {col.top_values.map((v, i) => <div key={i} className="dqc-topval"><span>{v.value || "∅"}</span><span className="dqc-muted">{fmt(v.count)}</span></div>)}
            </div>
          ) : <div className="dqc-empty">Select a column to see its profile.</div>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── run history ─────────────────────────── */
function HistoryTab({ entityId }: { entityId: string }) {
  const [runs, setRuns] = useState<ConformanceRun[]>([]);
  useEffect(() => { (async () => { try { const r = await sdh_conformanceHistory(entityId); setRuns(r.runs || []); } catch {} })(); }, [entityId]);
  return (
    <div className="dqc-tablewrap">
      <table className="dqc-table">
        <thead><tr><th>Run</th><th>Ruleset</th><th>When</th><th>Compiled</th><th>Headline</th></tr></thead>
        <tbody>{runs.map((r) => (
          <tr key={r.run_id} className="dqc-trow">
            <td><b>{r.run_id}</b></td><td>{r.ruleset_version}</td><td>{r.generated_at?.replace("T", " ").slice(0, 16)}</td>
            <td>{r.compiled_count}</td><td>{(r.conformance || {}).headline || "—"}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────── rule detail modal ─────────────────────────── */
// UI.3 — exported. v2 hosts the same modal stack as v1 (details → refine → versions → field
// values), so "Details & evidence" behaves identically on both screens.
export function RuleDetailModal({ entityId, appNm, rule, onClose, onDone, defs, onRefine, onFieldValues,
                          onVersions, previewFooter }:
  { entityId: string; appNm: string; rule: EnrichedRule; onClose: () => void; onDone: (m: string) => void;
    defs?: AssessmentDef[]; onRefine?: () => void; onFieldValues?: (t: string, c: string, codes?: string[]) => void;
    onVersions?: () => void;
    /** a rule that is NOT in the rulebook yet (SME "Add a rule" preview): read-only, this footer replaces the
     *  decision footer and nothing is measured again or saved from here */
    previewFooter?: React.ReactNode }) {
  const pol = policyOfRule(usePolicies(entityId), rule.rule_id);   // the policy / control this rule implements
  const [polPop, setPolPop] = useState(false);
  const [sa, setSa] = useState<any>(null);      // this rule's set-aside category (only when it is set aside)
  useEffect(() => {
    const hidden = flagsOf(rule).some((f) => HIDDEN_FLAGS.has(f)); setSa(null);
    if (!hidden) return;
    let live = true;
    sdh_setaside(entityId, ruleKey(rule)).then((r: any) => {
      if (live) setSa(Object.values(r?.rules || {})[0] || null); }).catch(() => {});
    return () => { live = false; };
  }, [entityId, ruleKey(rule)]); // eslint-disable-line
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  // default SQL dialect = MS-SQL; can later be driven by an env/config value
  const [dialect, setDialect] = useState<"tsql" | "postgres" | "oracle" | "duckdb">(
    ((window as any).__DQ_SQL_DIALECT__ as any) || "tsql");
  const [prev, setPrev] = useState<{ viol?: number; appl?: number; pass?: number | null; state: string } | null>(null);
  const [showSamples, setShowSamples] = useState(false);
  const [mAssess, setMAssess] = useState<string>((rule as any).sme_decision?.assessment || "");
  const [mComment, setMComment] = useState<string>((rule as any).sme_decision?.comment || "");
  const [showHistory, setShowHistory] = useState(false);
  const [showSql, setShowSql] = useState(false);
  const [mPop, setMPop] = useState(false);
  const raw: any = rule._raw || {};
  const bundle: any = (rule as any).sql_bundle;
  const im = prev || impactOf(rule);
  const [prevSamples, setPrevSamples] = useState<{ fails: any[]; passes: any[] } | null>(null);
  const doPreview = async () => {
    if (previewFooter) { onDone("This rule is a preview: it was measured when it was generated."); return; }
    setBusy(true); onDone("Measuring…");
    try {
      const res: any = await sdh_preview(entityId, appNm, [rule.rule_id], "all");
      const { hit, compiled } = readPreview(res, rule.rule_id);
      if (hit) {
        const viol = hit.violating_rows ?? hit.violations ?? 0;
        // never fall back to total_rows: it is the whole table, and a missing applicable count must
        // show as unknown, not as a plausible-looking number (it showed 12,128 for a 121-row rule)
        const appl = hit.applicable_rows ?? hit.applicable ?? null;
        setPrev({ state: viol > 0 ? "flags" : "pass", viol, appl,
                  pass: hit.pass_rate ?? (appl ? 1 - viol / appl : 1) });
        // F6.3 — capture the sample rows the preview returns, so the SME can inspect them
        // immediately after a preview even when nothing was stored on the rule.
        setPrevSamples({
          fails: hit.sample_fail_records || hit.sample_violation_keys || hit.samples || [],
          passes: hit.sample_pass_records || [],
        });
      } else onDone(compiled === 0 ? "This rule type can’t be auto-measured yet." : "No measurable rows.");
    } catch { onDone("Preview failed — showing the stored sample-run impact."); } finally { setBusy(false); }
  };
  const saveDecision = async () => {
    const def = mAssess ? ASSESSMENT_BY_VALUE[mAssess] : undefined;
    if (!def) { onDone("Pick a decision first."); return; }
    setBusy(true);
    try {
      await sdh_applyDecisions(entityId, appNm, [{
        constraint_id: rule.rule_id, decision_type: "rule_review",
        choice: def.choice, assessment: def.value, decision_gate: def.gate, comment: mComment,
        sme_id: user?.user_id || user?.email || "unknown",
        sme_name: user?.full_name || user?.email || undefined,
      }]);
      onDone(`Saved: ${def.label}`);
    } catch { onDone("Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="dqc-modalbg" onClick={onClose}>
      <div className="dqc-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="dqc-modalh">
          <div>
            <div className="dqc-muted" style={{ color: "#cdd8ea" }}>RULE DETAILS</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--dqc-disp)" }}>{rule.rule_name || rule.rule_id}</div>
            {/* F6 — business_summary as a subtitle right under the title (business-first).
                Show it whenever it's a real sentence, even if it echoes the title — the title is
                a short label, the summary is the assertion, and seeing both orients the SME. */}
            {(() => {
              const s = String((rule as any).business_summary || "").trim();
              const t = String(rule.rule_name || "").trim();
              // hide only if it's identical AND short (pure duplication of a label)
              if (!s) return null;
              if (s.toLowerCase() === t.toLowerCase() && s.length < 40) return null;
              return <div style={{ opacity: .92, fontSize: 13.5, marginTop: 3, maxWidth: 720, lineHeight: 1.4 }}>{s}</div>;
            })()}
            <div style={{ opacity: .6, fontFamily: "var(--dqc-mono)", fontSize: 11, marginTop: 3 }}>
              {rule.rule_id}
              {(rule as any).legacy_id ? <span style={{ opacity: .7, marginLeft: 8 }}>· was {(rule as any).legacy_id}</span> : null}
            </div>
            {(() => {
              const dv = dimensionOf(rule);
              const rc: any = (rule as any).review_classification || {};
              const ver = Number((rule as any).version || 1);
              const rf: any = (rule as any).refinement;
              const cb = confidenceBand(rule);
              return (
                <div className="dqc-hbadges">
                  {ver > 1 && <span className="hb ver">v{ver} active</span>}
                  {dv && <span className={"hb dim dim-" + dv.toLowerCase()}>{dv}</span>}
                  {/* F6 — plain-English confidence band instead of a number. */}
                  {cb && <span className={"hb conf conf-" + cb.tone} title={cb.why}>{cb.label}</span>}
                  {rc.risk && <span className="hb">{rc.risk_label || rc.risk} risk</span>}
                  {rf?.action && rf.action !== "KEEP" && <span className="hb rev">✎ agent revised</span>}
                  <span style={{ marginLeft: 2 }}><HelpDot topic="confidence" /></span>
                </div>
              );
            })()}
          </div>
          <button className="dqc-x" onClick={onClose}>×</button>
        </div>
        <div className="dqc-modalbody rd2">
        {/* RD2 ① RECOMMENDATION — where the rule stands and what to do, in one sentence */}
        {(() => {
          const raw0: any = (rule as any)._raw || {};
          const fl = flagsOf(rule); const hid = fl.filter((f) => HIDDEN_FLAGS.has(f));
          const pend = pendingProposal(rule);
          const ia0: any = raw0.impact_assessment || {};
          const meas = ia0.executed ? `${fmt(ia0.violations)} of ${fmt(ia0.applicable_rows)} rows flagged` : null;
          const disp = String((rule as any).disposition || raw0.disposition || "");
          const why0 = String((rule as any).disposition_reason || raw0.disposition_reason || "");
          let tone = "rev", txt: any;
          if (hid.length && sa) { tone = "sa " + sa.category; txt = <><b>Set aside · {sa.label}</b> — {sa.action}
            {(sa.evidence || []).length > 0 && <ul className="rd2-sa-ev">{sa.evidence.map((e: string, i: number) => <li key={i}>{e}</li>)}</ul>}</>; }
          else if (hid.length) { tone = "warn"; txt = <>⚑ <b>Kept out of the review queue</b> — {FLAG_LABEL[hid[0]] || hid[0]}{flagNotesOf(rule)[hid[0]] ? `: ${flagNotesOf(rule)[hid[0]]}` : ""}. Usually reject it, or refine it if the intent is right.</>; }
          else if (pend) { tone = "info"; txt = <>✎ <b>A proposed change (v{pend.version}) waits for your decision</b> ({pend.source}). Compare it in the history before approving.</>; }
          else if (disp === "AUTO_EXECUTE") { tone = "ok"; txt = <>✓ <b>Pre-approved on evidence</b>{meas ? ` — ${meas}` : ""}. Confirm it, or refine it if the scope is wrong.</>; }
          else { txt = <><b>Needs your decision</b>{why0 ? ` — ${why0.replace(/^Not pre-approved on evidence:\s*/i, "not pre-approved: ")}` : ""}. Check what it checks and the failing rows.</>; }
          return <div className={"rd2-rec " + tone}>{txt}</div>;
        })()}
          <div className="rd2-grid">
            <div className="rd2-main">
              <section className="rd2-sec"><div className="rd2-h">② What it checks</div>
          {/* ═══ F6: BUSINESS-FIRST — plain English, then why this rule should exist ═══ */}
          {(() => {
            const detail = String((rule as any).business_detail || "").trim();
            const plain = String((rule as any).plain || "").trim();
            const text = detail || plain;
            if (!text) return null;
            return <div className="dqc-plain">
              <p className="dqc-p big">{text}</p>
            </div>;
          })()}

              {(() => {
                const raw0: any = (rule as any)._raw || {};
                const mf: string[] = raw0.merged_from || ((raw0.consolidation_group || {}).members || []).map((m: any) => m.constraint_id);
                const cv: string[] = raw0.covers || [];
                if (!mf?.length && !cv?.length) return null;
                return <p className="rd2-note">{mf?.length ? <>Combines {mf.length} rules found separately. </> : null}
                  {cv?.length ? <>Also covers {cv.length} narrower rule(s): {cv.join(", ")}.</> : null}</p>;
              })()}
<div className="k">Exactly what executes</div>
              <RuleLogicView rule={rule} fallback={formatPseudo(rule)} />

                            </section>
              <section className="rd2-sec"><div className="rd2-h">③ On your data</div>
                    {(() => {
            const ia: any = (rule as any).review_classification?.impact || (rule as any)._raw?.impact_assessment || {};
            const measured = prev || (ia.executed ? { appl: ia.applicable_rows, viol: ia.violations, rate: ia.violation_rate } : null);
            if (!measured && im.state === "unsupported")
              return <p className="dqc-muted">This rule type isn’t executable in the engine yet.</p>;
            if (!measured && im.state === "not_applicable")
              return <div className="dqc-impact un" style={{ marginBottom: 4 }}>
                <span className="untext">{(im as any).note || `Executed on the sample, but the rule’s condition matches 0 of ${fmt((ia.applicable_rows ?? 0) || rule.impact_applicable_rows)} rows in this data — nothing to flag here.`}</span>
              </div>;
            if (!measured && (ia.error || ia.status === "not_executable") && im.state !== "not_applicable")
              return <div className="dqc-impact un" style={{ marginBottom: 4, borderLeft: "3px solid var(--r,#c0392b)", paddingLeft: 8 }}>
                <span className="untext">{(rule as any).tag_notes?.execution || ia.note || ia.reason || ia.error || "This rule could not be executed — see the reason above; it stays in review and won’t block others."}</span>
              </div>;
            if (!measured && im.state === "unmeasured")
              return <div className="dqc-impact un" style={{ marginBottom: 4 }}>
                <span className="untext">Not assessed yet — preview how many records this would flag.</span>
                <button className="dqc-mini" disabled={busy} onClick={doPreview}>Preview impact</button>
              </div>;
            // Final gate: a fresh live preview (`prev`) is always trustworthy; otherwise the stored
            // numbers must belong to THIS version of the rule. Never fall back to a previous version's
            // figures just because the fields still hold them.
            const mm = measurementOf(rule);
            if (!prev && !mm.ok) {
              return <div style={{ marginBottom: 6 }}>
                <NotMeasured m={mm} />
                <button className="dqc-mini" style={{ marginTop: 8 }} disabled={busy}
                  onClick={doPreview}>Measure this version now</button>
              </div>;
            }
            const appl = (measured as any).appl ?? (im as any).appl ?? rule.impact_applicable_rows;
            const viol = (measured as any).viol ?? (im as any).viol ?? rule.impact_violations;
            const rate = (measured as any).rate;
            return <>
              <div className="dqc-kpis">
                <div><span>{fmt(appl)}</span><small>records checked</small></div>
                <div><span className={viol ? "r" : "g"}>{fmt(viol)}</span><small>flagged</small></div>
                <div><span className={viol ? "r" : "g"}>{rate != null ? (rate * 100).toFixed(1) + "%" : pctf((im as any).pass ?? rule.impact_pass_rate)}</span><small>{rate != null ? "violation rate" : "pass rate"}</small></div>
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="dqc-mini" disabled={busy} onClick={doPreview}>Re-run preview</button>
                {/* Right where a violation rate raises "but what IS in that field?" */}
                {(() => {
                  const own = String((rule as any).owner_field || (rule as any)._raw?.owner_field_id || "");
                  const [t, c] = own.split("::");
                  return t && c ? <button className="dqc-mini vals" onClick={() => onFieldValues?.(t, c)}
                    title="Profiled values, your reference data, and what was accepted">⌕ What values are in {c}?</button> : null;
                })()}
              </div>
            </>;
          })()}
          {(() => {
            const rc: any = (rule as any).review_classification?.impact || {};
            const raw: any = (rule as any)._raw || {};
            const ia: any = raw.impact_assessment || {};
            // pick the first NON-EMPTY array — an empty [] is truthy in JS, so `||` alone would
            // stop at the first (empty) source and never reach the impact_assessment fallback.
            const pick = (...arrs: any[]) => arrs.find((a) => Array.isArray(a) && a.length) || [];
            const fails: any[] = pick(prevSamples?.fails, (rule as any).impact_sample_fail, ia.sample_fail_records, rc.sample_fail_records);
            const passes: any[] = pick(prevSamples?.passes, (rule as any).impact_sample_pass, ia.sample_pass_records, rc.sample_pass_records);
            if (!fails.length && !passes.length) return null;
            return <div className="dqc-samples">
              <button className="dqc-sampletoggle" onClick={() => setShowSamples((v) => !v)}>
                <span className="chev">{showSamples ? "▾" : "▸"}</span>
                <span>Sample records</span>
                <span className="dqc-samplecount">
                  {fails.length ? <em className="f">{fails.length} failing</em> : null}
                  {passes.length ? <em className="p">{passes.length} passing</em> : null}
                </span>
                <span className="hint">click to inspect the actual rows</span>
              </button>
              {showSamples && (
                <SampleRecordsPanel
                  title={rule.rule_name || rule.rule_id}
                  subtitle={(rule as any).owner_field || raw.owner_field_id}
                  fails={fails} passes={passes}
                  ruleFields={(raw.participating_fields || []).map((p: any) => p.field_id).filter(Boolean)}
                  onClose={() => setShowSamples(false)} />
              )}
            </div>;
          })()}
              </section>
              {(flagsOf(rule).length > 0 || hasCaveat(rule)) && <section className="rd2-sec warn"><div className="rd2-h">④ Concerns</div>
{flagsOf(rule).length > 0 && (
                <div className="rlv-flagnotes">
                  {flagsOf(rule).map((f) => (
                    <div key={f}><b>⚑ {FLAG_LABEL[f] || f}</b>{flagNotesOf(rule)[f] ? ` — ${flagNotesOf(rule)[f]}` : ""}</div>
                  ))}
                </div>
              )}
              {hasCaveat(rule) && (
                <div className="dqc-caveat">
                  <div className="ch">⚠ The model’s own caveat <span className="verdict">{(rule as any).challenge?.verdict}</span></div>
                  {(rule as any).challenge?.issue && <p>{(rule as any).challenge.issue}</p>}
                  {(rule as any).challenge_rationale && <p className="rat">{(rule as any).challenge_rationale}</p>}
                </div>
              )}
              </section>}
              <section className="rd2-sec"><div className="rd2-h">⑤ Why it matters</div>
          {(() => {
            // "Why this rule" — the business relevance the SME reads FIRST. Consolidates policy
            // basis, business impact, the agent's reasoning and the confidence band into one card
            // so the reviewer isn't hunting across four boxes.
            const g: any = (rule as any).generation_rationale || {};
            const bi: any = (rule as any).business_impact;
            const impacts: string[] = Array.isArray(bi) ? bi.filter(Boolean) : (bi ? [String(bi)] : []);
            const basis = g.policy_basis || (rule as any).policy_basis;
            const reason = g.business_reason && g.business_reason !== basis ? g.business_reason : null;
            const rationale = g.rationale && g.rationale !== (rule as any).challenge_rationale ? g.rationale : null;   // shown under Concerns already
            const cb = confidenceBand(rule);
            if (!basis && !impacts.length && !reason && !rationale && !cb) return null;
            return (
              <div className="dqc-why2">
                {basis && <p className="dqc-p"><b>Policy basis:</b> {basis}</p>}
                {impacts.length > 0 && <>
                  <div className="sub">What goes wrong if it's violated</div>
                  <ul className="dqc-ul">{impacts.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </>}
                {(reason || rationale) && <div className="agentbox">
                  <div className="ch">💡 The agent's reasoning</div>
                  {reason && <p>{reason}</p>}
                  {rationale && <p className="rat">{rationale}</p>}
                </div>}
                {cb && <div className="confrow">
                  <span className={"confbadge c-" + cb.tone}>{cb.label}</span>
                  <span className="confwhy">{cb.why}</span>
                </div>}
              </div>
            );
          })()}

              </section>
            </div>
            <aside className="rd2-side">
          {(() => {
            const dec: any = (rule as any).sme_decision || {};
            const rc: any = (rule as any).review_classification || {};
            const gate = dec.decision_gate as keyof typeof GATE_TONE | undefined;
            if (!gate && !rc.trust) return null;
            return (
              <div className="dqc-review">
                {gate ? (
                  <div className="dqc-smebox" style={{ ["--t" as any]: GATE_TONE[gate] }}>
                    <div className="hd">
                      <span className="gate">{String(gate)}</span>
                      <span className="cat">{ASSESSMENT_BY_VALUE[dec.assessment]?.label || dec.assessment || ""}</span>
                      <span className="who">
                        {dec.sme_name || dec.sme_id || "SME"}
                        {dec.decided_at ? ` · ${String(dec.decided_at).replace("T", " ").slice(0, 16)}` : ""}
                        {(rule as any).version ? ` · reviewed at v${dec.reviewed_version ?? (rule as any).version}` : ""}
                      </span>
                    </div>
                    {dec.comment && <p className="cmt">“{dec.comment}”</p>}
                  </div>
                ) : (
                  <div className="dqc-smebox none"><div className="hd"><span className="gate">NOT REVIEWED</span>
                    <span className="cat">No decision recorded yet</span></div></div>
                )}
                {rc.trust && (
                  <div className="dqc-triage">
                    <span className={"chip trust t-" + String(rc.trust).toLowerCase()} title={rc.explain?.trust}><b>Trust</b> {rc.trust_label}</span>
                    <span className={"chip risk k-" + String(rc.risk).toLowerCase()} title={rc.explain?.risk}><b>Risk</b> {rc.risk_label}</span>
                    {rc.challenge_severity && rc.challenge_severity !== "None" && <span className="chip sev" title={rc.explain?.reason}><b>Objection</b> {rc.challenge_severity}</span>}
                    {rc.review_reason && <span className="chip" title={rc.explain?.reason}>{rc.review_label}</span>}
                    <HelpDot topic="trust" />
                  </div>
                )}
              </div>
            );
          })()}
              <section className="rd2-sec"><div className="rd2-h">Where this rule comes from</div>
              {(() => {
                // WHERE THIS RULE COMES FROM - only what genuinely differs per rule (the BC-derived "Authority"
                // row was the same for nearly every rule; "Evidence" repeated "On your data")
                const r0: any = (rule as any)._raw || {};
                const ch: any = (rule as any).challenge || r0.challenge || {};
                const et = String((rule as any).evidence_type || r0.evidence_type || r0.classification?.evidence_type || "");
                const ORIGIN: Record<string, string> = { profiling_evidence: "Found in your data — a measured pattern",
                  sap_standard: "SAP standard knowledge", domain_knowledge: "Domain knowledge (industry practice)",
                  client_documented: "Your documentation (code meanings / data dictionary)" };
                const BASIS: Record<string, string> = { client_documented: "Client-documented — your own code meanings / rules say so",
                  sap_standard: "SAP standard behaviour", business_invariant: "A business invariant (holds for any company in this business)",
                  current_state_pattern: "A current-state pattern — it holds in today's data, but nothing requires it",
                  client_specific: "Client-specific practice" };
                const kb = r0.knowledge_basis || (rule as any).knowledge_basis;
                const de = String(r0.data_evidence || (rule as any).data_evidence || "");
                const DE: Record<string, string> = { supported: "the data supports it", contradicted: "the data contradicts it",
                  observed: "the pattern is visible in the sample", not_observed: "not visible in the sample — nothing to confirm it yet" };
                const grp: any = r0.consolidation_group || {};
                const nMerged = (grp.members || []).length;
                const ccm = r0.condition_code_meaning || (rule as any).condition_code_meaning;
                const verdict = ch.verdict ? String(ch.verdict).toUpperCase() : "";
                const issue = ch.issue_type && ch.issue_type !== "none" ? String(ch.issue_type).replace(/_/g, " ") : "";
                return <div className="dqc-kv">
                  {et && <div className="kv"><span className="kk">Origin</span><span className="kvv">{ORIGIN[et] || et.replace(/_/g, " ")}
                    {nMerged > 1 ? ` · consolidated from ${nMerged} rules` : ""}</span></div>}
                  {kb && <div className="kv"><span className="kk">Knowledge</span><span className="kvv">{String(kb)}{de && DE[de] ? <em className="rd2-de"> — {DE[de]}</em> : null}</span></div>}
                  {ccm && <div className="kv"><span className="kk">Codes mean</span><span className="kvv">{String(ccm)}</span></div>}
                  {ch.basis && <div className="kv"><span className="kk">Basis</span><span className="kvv">{BASIS[ch.basis] || ch.basis}</span></div>}
                  {verdict && <div className="kv"><span className="kk">Reviewer agent</span><span className="kvv">{verdict}
                    {issue ? (verdict === "KEEP" ? ` — its “${issue}” concern did not hold on the data` : ` — ${issue}`) : ""}</span></div>}
                  {(rule as any).policy_basis && <div className="kv"><span className="kk">Policy basis</span><span className="kvv">{(rule as any).policy_basis}</span></div>}
                  {pol && <div className="kv"><span className="kk">Policy</span><span className="kvv"><button className="rd2-link" onClick={() => setPolPop(true)} title="Open the policy: controls, rules, history, decision">{pol.policy.name} ↗</button> <span className="phys">{pol.policy.policy_id}</span> · {pol.policy.status}
                    {polPop && <PolicyPopup entityId={entityId} policyId={pol.policy.policy_id} onClose={() => setPolPop(false)} />}
                    {pol.control && <div className="dqc-muted">control: {pol.control.type}, {String(pol.control.kind).replace(/_/g, " ")}{pol.control.owner_role ? ` · owner ${pol.control.owner_role}` : ""}</div>}</span></div>}
                </div>;
              })()}
{(() => {
                const as_ = (rule as any).tags?.authority_state;
                const head = as_ === "client_rule_confirmed" ? "Confirmed by your data-dictionary rule"
                  : as_ === "values_client_governed" ? "Values governed by client reference data"
                  : as_ === "column_client_governed" ? "Column governed by client — values pending"
                  : ((rule as any).policy_status === "backed_confirmed" || (rule as any).rule_logic_authority === "client_confirmed") ? "Backed by client-confirmed policy" : null;
                if (!head) return null;
                const pending = as_ === "column_client_governed";
                return (
                  <div className={"dqc-clientok" + (pending ? " pending" : "")}>
                    <div className="k">{pending ? "◑" : "✓"} {head}</div>
                    {(rule as any).tag_notes?.authority && <p className="dqc-p">{(rule as any).tag_notes.authority}</p>}
                    {(rule as any).policy_citation && <p className="dqc-p">{String((rule as any).policy_citation)}</p>}
                    {(rule as any).business_control_id && <p className="dqc-p"><b>Evidence:</b> control <code>{(rule as any).business_control_id}</code></p>}
                  </div>
                );
              })()}
              {(() => {
                const cc = (rule as any).field_notes, kg = (rule as any).known_gaps;
                if (!cc && !kg) return null;
                return <div className="dqc-fieldnotes" style={{ marginTop: 10, padding: "8px 10px", borderLeft: "3px solid var(--dqc-accent,#0a7a6f)", background: "var(--dqc-soft,#f5faf9)", borderRadius: 6 }}>
                  <div className="k" style={{ marginBottom: 4 }}>Field notes <span className="dqc-muted" style={{ fontWeight: 400 }}>(client data dictionary)</span></div>
                  {cc && <p className="dqc-p" style={{ margin: "2px 0" }}>{String(cc).slice(0, 400)}</p>}
                  {kg && <p className="dqc-p" style={{ margin: "2px 0", color: "var(--dqc-warn,#a15c00)" }}><b>Known data gap:</b> {String(kg).slice(0, 300)}</p>}
                </div>;
              })()}

                            </section>
              <section className="rd2-sec"><div className="rd2-h">Fields involved <em className="hintk">click to see values</em></div>
              <div className="rd2-fields">{meaningfulFields(rule).map((f, i) => (
                <button key={i} className="dqc-fchip act" title="See this field's values: profiled, client-supplied and accepted"
                  onClick={() => { const [t, c] = String(f.fid).split("::"); if (t && c) onFieldValues?.(t, c, codesOfField(rule, f.fid)); }}>
                  <span className="ic">⌕</span>{f.label} <span className="phys">{f.fid.replace("::", ".")}</span>
                </button>))}</div>
              </section>
              <div className="dqc-sqlwrap">
                <button className="dqc-sqltoggle" onClick={() => setShowSql((v: boolean) => !v)}>
                  <span className="chev">{showSql ? "▾" : "▸"}</span>
                  <span>Executable SQL</span>
                  <span className="dqc-muted" style={{ fontWeight: 400 }}>engine-generated — for your developers</span>
                  <HelpDot topic="anatomy" />
                </button>
                {showSql && <>
                  <div className="k" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                    <span className="dqc-muted" style={{ fontWeight: 400 }}>dialect</span>
                    {bundle && (
                      <select className="dqc-dialect" value={dialect} onChange={(e) => setDialect(e.target.value as any)}>
                        <option value="tsql">MS-SQL</option>
                        <option value="postgres">PostgreSQL</option>
                        <option value="oracle">Oracle</option>
                        <option value="duckdb">DuckDB</option>
                      </select>
                    )}
                  </div>
                  {bundle?.[dialect]?.sample_query
                    ? <pre className="dqc-code">{bundle[dialect].sample_query}</pre>
                    : <p className="dqc-muted">This rule type isn’t compiled to SQL yet — see “What it enforces”.</p>}
                </>}
              </div>

              <div className="dqc-histsec rd2-hist">
          <div className="k" style={{ margin: "0 0 8px" }}>History</div>
          <div className="dqc-histrow">
          {(() => {
            const revs: any[] = (rule as any).revisions || [];
            const ver = Number((rule as any).version || 1);
            const changes = revs.filter((r: any) => r.version === ver && !r.is_base)[0];
            const nChanged = changes ? Object.keys(changes.diff || {}).length : 0;
            return (
              <button className="dqc-histcard" onClick={() => setShowVersions(true)}>
                <i className="ti ti-git-branch" aria-hidden="true" />
                <div className="hc-body">
                  <div className="hc-title">Rule versions</div>
                  <div className="hc-sub">
                    {revs.length <= 1
                      ? "Single version — no changes yet"
                      : <>Now on <span className="hc-v">v{ver}</span> · {revs.length} versions{nChanged ? ` · ${nChanged} change${nChanged > 1 ? "s" : ""} from base` : ""}</>}
                  </div>
                </div>
                <span className="hc-open">View history <i className="ti ti-external-link" aria-hidden="true" /></span>
              </button>
            );
          })()}
          {(() => {
            const hist: any[] = (rule as any).decision_history || [];
            const dec: any = (rule as any).sme_decision || {};
            const latest = hist[0] || dec;
            const gate = String(latest.decision_gate || "").toUpperCase();
            const tone = GATE_TONE[gate as keyof typeof GATE_TONE] || "#8a97a3";
            const label = ASSESSMENT_BY_VALUE[latest.assessment]?.label || latest.assessment || gate;
            const n = hist.length || (dec.decided_at ? 1 : 0);
            return (
              <button className="dqc-histcard" onClick={() => setShowStatus(true)}>
                <i className="ti ti-message-circle" aria-hidden="true" />
                <div className="hc-body">
                  <div className="hc-title">Status &amp; feedback</div>
                  <div className="hc-sub">
                    {n === 0 ? "No decisions yet"
                      : <>Latest: <span style={{ color: tone, fontWeight: 500 }}>{label}</span>{latest.comment ? ` · "${String(latest.comment).slice(0, 40)}${String(latest.comment).length > 40 ? "…" : ""}"` : ""} · {n} entr{n === 1 ? "y" : "ies"}</>}
                  </div>
                </div>
                <span className="hc-open">View history <i className="ti ti-external-link" aria-hidden="true" /></span>
              </button>
            );
          })()}
          </div>
        </div>

            </aside>
          </div>
        </div>
        {previewFooter ? <div className="dqc-modalf">{previewFooter}</div> : (
        <div className="dqc-modalf">
          <span style={{ flex: 1 }} />
          {onRefine && <button className="dqc-btn info" onClick={onRefine} title="Send this rule to the agent with your instruction">✨ Refine with agent</button>}
          <DecideButton draft={mAssess ? { assessment: mAssess, comment: mComment } : undefined}
            current={(rule as any).sme_decision} onOpen={() => setMPop(true)} />
          <button className="dqc-btn app" disabled={busy || !mAssess || !mComment.trim()} onClick={saveDecision}>{busy ? "Saving…" : "Save decision"}</button>
        </div>)}
        {mPop && <DecisionPopover defs={defs} anchorLabel={rule.rule_name || rule.rule_id}
          value={mAssess} comment={mComment} onCancel={() => setMPop(false)}
          onCommit={(a, c) => { setMAssess(a); setMComment(c); setMPop(false); }} />}
        {showVersions && <VersionsPopup rule={rule} onClose={() => setShowVersions(false)} onCompare={onVersions} />}
        {showStatus && <StatusPopup rule={rule} onClose={() => setShowStatus(false)} />}
      </div>
    </div>
  );
}

/* ─────────────── Rule versions popup (full timeline) ─────────────── */
function VersionsPopup({ rule, onClose, onCompare }: { rule: EnrichedRule; onClose: () => void; onCompare?: () => void }) {
  const revs: any[] = (rule as any).revisions || [];
  const vn = Number((rule as any).version || 1);
  const CT: any = { generated: "Original rule", sme_refinement: "Agent refinement", lov_update: "Reference-data update", manual_edit: "Manual edit" };
  const DK: any = { business_summary: "Title", business_detail: "Description", expected_outcome: "What it enforces", predicate_template: "Rule logic", condition: "When it applies", constraint_family: "Rule type", allowed_values: "Allowed values", severity: "Severity", condition_code_meaning: "Trigger" };
  return (
    <div className="dqc-hp-bg" onClick={onClose}>
      <div className="dqc-hp" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="dqc-hp-h"><i className="ti ti-git-branch" aria-hidden="true" /><span>Rule versions</span>
          <span className="dqc-hp-sub">content changes</span>
          <button className="dqc-hp-x" onClick={onClose}>×</button></div>
        <div className="dqc-hp-body">
          {!revs.length ? <div className="dqc-muted">Single version — no changes yet.</div> : (
            <div className="vtl">
              <div className="vtl-line" />
              {revs.slice().sort((a: any, b: any) => (b.version || 0) - (a.version || 0)).map((rev: any) => {
                const isAct = rev.version === vn; const ia2 = rev.impact || {}; const df = rev.diff || {};
                return (
                  <div key={rev.version} className={"vtl-item" + (isAct ? " active" : "")}>
                    <div className={"vtl-dot" + (isAct ? " on" : "")} />
                    <div className="vtl-body">
                      <div className="vtl-meta">
                        <span className={"vtl-vn" + (isAct ? " act" : "")}>v{rev.version}</span>
                        {rev.is_base && <span className="vtl-base">base</span>}
                        {isAct && <span className="vtl-badge">active</span>}
                        <span className="vtl-type">{CT[rev.change_type] || rev.change_type}</span>
                        <span className="vtl-when">{rev.author} · {String(rev.at || "").replace("T", " ").slice(0, 16)}</span>
                      </div>
                      {rev.summary && <div className="vtl-sum">{rev.summary}</div>}
                      {ia2.applicable_rows != null && (
                        <div className="vtl-impact">{(ia2.violations || 0) === 0
                          ? <span className="g">Clean — {(ia2.applicable_rows || 0).toLocaleString()} pass</span>
                          : <span className="r">Flags {(ia2.violations || 0).toLocaleString()} of {(ia2.applicable_rows || 0).toLocaleString()}</span>}</div>
                      )}
                      {Object.keys(df).length > 0 && (
                        <details className="vtl-diff"><summary>{Object.keys(df).length} field(s) changed</summary>
                          {Object.entries(df).map(([k2, d2]: [string, any]) => (
                            <div key={k2} className="vtl-d"><span className="vtl-dk">{DK[k2] || k2}</span>
                              <div className="vtl-from">{String((d2 as any)?.from ?? "(empty)").slice(0, 140)}</div>
                              <div className="vtl-to">{String((d2 as any)?.to ?? "(empty)").slice(0, 140)}</div></div>
                          ))}
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {onCompare && revs.length > 1 && <div style={{ marginTop: 10 }}><button className="hlink" onClick={() => { onClose(); onCompare(); }}>Compare versions and switch active →</button></div>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Status & feedback popup (full timeline) ─────────────── */
function StatusPopup({ rule, onClose }: { rule: EnrichedRule; onClose: () => void }) {
  const dec: any = (rule as any).sme_decision || {};
  const ledger: any[] = (rule as any).decision_history || [];
  const all = ledger.length ? ledger : (dec.decided_at ? [dec] : []);
  const sorted = [...all].sort((a, b) => String(b.decided_at || "").localeCompare(String(a.decided_at || "")));
  return (
    <div className="dqc-hp-bg" onClick={onClose}>
      <div className="dqc-hp" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="dqc-hp-h"><i className="ti ti-message-circle" aria-hidden="true" /><span>Status &amp; feedback history</span>
          <span className="dqc-hp-sub">decisions, comments, status changes</span>
          <button className="dqc-hp-x" onClick={onClose}>×</button></div>
        <div className="dqc-hp-body">
          {!sorted.length ? <div className="dqc-muted">No decisions recorded yet.</div> : (
            <div className="stl">
              <div className="stl-line" />
              {sorted.map((d: any, i: number) => {
                const gate = String(d.decision_gate || "").toUpperCase();
                const tone = GATE_TONE[gate as keyof typeof GATE_TONE] || "#8a97a3";
                const label = ASSESSMENT_BY_VALUE[d.assessment]?.label || d.assessment || gate || d.choice;
                return (
                  <div key={i} className="stl-item">
                    <div className="stl-dot" style={{ background: tone, borderColor: tone }} />
                    <div className="stl-body" style={{ borderLeftColor: tone }}>
                      <div className="stl-meta">
                        <span className="stl-gate" style={{ background: tone }}>{gate || "COMMENT"}</span>
                        <span className="stl-label" style={{ color: tone }}>{label}</span>
                        {d.reviewed_version && <span className="stl-v">on v{d.reviewed_version}</span>}
                        <span className="stl-when">{d.sme_name || d.sme_id || "SME"} · {String(d.decided_at || "").replace("T", " ").slice(0, 16)}</span>
                      </div>
                      {d.comment && <p className="stl-cmt">"{d.comment}"</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Row({ k, v }: { k: string; v?: any }) {
  return <div className="dqc-row2"><span className="rk">{k}</span><span className="rv">{v == null || v === "" ? "—" : String(v)}</span></div>;
}
function confidenceBand(rule: EnrichedRule): { label: "Strong" | "Moderate" | "Tentative"; tone: string; why: string } | null {
  const g: any = (rule as any).generation_rationale || {};
  const ev = String(g.evidence_type || (rule as any).review_classification?.impact?.evidence_type || "").toLowerCase();
  const conf = typeof g.confidence === "number" ? g.confidence : null;
  if (!ev && conf == null) return null;
  const strong = ["sap_standard", "profiling_evidence", "measured", "client_confirmed"];
  const weak = ["semantic_inference", "domain_knowledge", "heuristic", "inferred"];
  let label: "Strong" | "Moderate" | "Tentative";
  if (strong.some((s) => ev.includes(s))) label = "Strong";
  else if (weak.some((s) => ev.includes(s))) label = conf != null && conf >= 0.7 ? "Moderate" : "Tentative";
  else label = "Moderate";
  if (label === "Strong" && conf != null && conf < 0.5) label = "Moderate";
  if (label === "Tentative" && conf != null && conf >= 0.8) label = "Moderate";
  const tone = label === "Strong" ? "g" : label === "Moderate" ? "s" : "sl";
  const why = label === "Strong" ? "Backed by an SAP-standard pattern or measured evidence."
    : label === "Moderate" ? "A reasonable inference, but not confirmed policy."
    : "A heuristic/domain-knowledge guess — review carefully.";
  return { label, tone, why };
}
function hasCaveat(rule: EnrichedRule): boolean {
  const ch: any = (rule as any).challenge;
  const v = String(ch?.verdict || "").toLowerCase();
  return !!ch && (v === "revise" || v === "reject" || !!ch.issue);
}
function evidenceLines(rule: EnrichedRule): string[] {
  const out: string[] = [];
  const ed: any = (rule as any).evidence_detail;
  if (ed && typeof ed === "object") {
    if (ed.value != null) out.push(`Condition “${ed.value}” holds on ${Number(ed.support || 0).toLocaleString()} records; target field is filled ${ed.fill_rate != null ? Math.round(ed.fill_rate * 100) + "%" : "—"} of the time (verdict: ${ed.verdict || "—"}).`);
  }
  const av: any[] = (rule as any).allowed_values;
  if (Array.isArray(av) && av.length) out.push(`Governed values (${av.length}): ${av.slice(0, 18).join(", ")}${av.length > 18 ? " …" : ""}.`);
  const ood: any[] = (rule as any).out_of_domain_values;
  if (Array.isArray(ood) && ood.length) out.push(`Found off-list in your data: ${ood.join(", ")} — ${Number((rule as any).out_of_domain_row_count || 0).toLocaleString()} record(s).`);
  return out;
}
function meaningfulFields(rule: EnrichedRule): { label: string; fid: string }[] {
  const fl: any[] = (rule as any).field_labels || [];
  const seen = new Set<string>();
  const out: { label: string; fid: string }[] = [];
  fl.forEach((f) => {
    const role = String(f.role || "").toLowerCase();
    if (role === "join" || role === "grain") return;       // plumbing, not business fields
    const fid = String(f.field_id || "");
    if (!fid || seen.has(fid)) return;
    seen.add(fid);
    out.push({ label: f.label || fid.split("::").pop(), fid });
  });
  if (!out.length) {
    const own = String(rule.owner_field || "");
    out.push({ label: (rule as any).owner_label || own.split("::").pop(), fid: own });
  }
  return out;
}
function humanizeLogic(text?: string | null, rule?: EnrichedRule): string {
  if (!text) return "";
  const fl: any[] = ((rule as any)?.field_labels) || [];
  const pairs: [string, string][] = [];
  fl.forEach((f) => {
    const fid = String(f.field_id || "");
    if (!fid.includes("::") || !f.label) return;
    const [t, c] = fid.split("::");
    const rep = `${t}."${f.label}"`;                 // consistent: <table>."Display Name"
    pairs.push([`${t}."${c}"`, rep], [`${t}::${c}`, rep], [`${t}.${c}`, rep]);
  });
  pairs.sort((a, b) => b[0].length - a[0].length);   // longest tokens first (quoted before bare)
  let out = String(text);
  pairs.forEach(([tok, rep]) => { out = out.split(tok).join(rep); });
  return out;
}

// Pretty-print the rule's CASE-WHEN pseudo-code (business field names, kept as-is) for readability.
// Deterministic English proved lossy on compound predicates, so we format the pseudo-code instead.
function formatPseudo(rule?: EnrichedRule): string {
  if (!rule) return "—";
  const raw: any = (rule as any)._raw || rule;
  // Prefer the CASE-WHEN condition (business_logic / predicate) — the SELECT-style sql_bundle.readable
  // is a query, not the rule logic, and doesn't format into readable pseudocode.
  let s = humanizeLogic((rule as any).business_logic || raw.business_logic || raw.predicate_template, rule)
        || (rule as any).sql_bundle?.readable;
  if (!s) { const eo = humanizeLogic((rule as any).expected_outcome, rule); return eo || rule.description || "—"; }
  s = String(s).replace(/\s+/g, " ").trim();
  s = s
    .replace(/\bCASE\b/g, "CASE")
    .replace(/\s*\bWHEN\b/g, "\n  WHEN ")
    .replace(/\s+\bAND\b/g, "\n    AND ")
    .replace(/\s+\bOR\b/g, "\n    OR ")
    .replace(/\s*\bTHEN\b/g, "\n  THEN ")
    .replace(/\s*\bELSE\b/g, "\n  ELSE ")
    .replace(/\s*\bEND\b/g, "\nEND")
    .replace(/\bSELECT\b/g, "SELECT")
    .replace(/\s+\bFROM\b/g, "\nFROM ")
    .replace(/\s+\bWHERE\b/g, "\nWHERE ");
  return s.replace(/ +\n/g, "\n").replace(/\n{2,}/g, "\n").trim();
}
function authText(rule: EnrichedRule): string {
  const a = authOf(rule);
  return { confirmed: "Client-confirmed policy", observed: "Observed in your data", inferred: "Inferred from enterprise knowledge", none: "Novel — needs your confirmation" }[a];
}

/* ─────────────────────────── styles ─────────────────────────── */
function DataGapsPanel({ gaps, onClose }: { gaps: CompletenessFinding[]; onClose: () => void }) {
  const [q, setQ] = useState("");
  const byTable = groupBy(gaps.filter((g) => !q || `${g.label} ${g.column} ${g.table}`.toLowerCase().includes(q.toLowerCase())), (g) => g.table);
  return (
    <div className="dqc-modalbg" onClick={onClose}>
      <div className="dqc-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(760px,96vw)" }}>
        <div className="dqc-modalh">
          <div>
            <div className="dqc-muted" style={{ color: "#cdd8ea" }}>DATA-COMPLETENESS REPORT</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--dqc-disp)" }}>{gaps.length} governed fields are unpopulated</div>
            <div style={{ opacity: .8, fontSize: 12, color: "#cdd8ea" }}>Defined/controlled by the model but ~100% empty in your data. A data-maintenance finding, not a rule to approve.</div>
          </div>
          <button className="dqc-x" onClick={onClose}>×</button>
        </div>
        <div className="dqc-modalbody">
          <div className="dqc-search" style={{ marginBottom: 12 }}>🔎<input value={q} placeholder="Find a field…" onChange={(e) => setQ(e.target.value)} /></div>
          {Object.keys(byTable).sort().map((t) => (
            <div key={t} style={{ marginBottom: 14 }}>
              <div className="dqc-fhead">{t} · {byTable[t].length}</div>
              <table className="dqc-table">
                <thead><tr><th>Field</th><th>Physical</th><th>Empty</th></tr></thead>
                <tbody>{byTable[t].map((g) => (
                  <tr key={g.field_id}>
                    <td><b>{g.label || g.column}</b></td>
                    <td className="dqc-muted" style={{ fontFamily: "var(--dqc-mono)" }}>{g.table}.{g.column}</td>
                    <td style={{ fontFamily: "var(--dqc-mono)", color: "var(--red)" }}>{g.null_pct}%</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// UI.3 — exported so SdhReviewWorkspace_v2 renders inside the SAME stylesheet. The v2 shell
// must not restate these rules; if it did, the two screens would drift apart on the next edit.
export const CSS = `
.dqc-gatebar.hide{display:none}
.dqc{--ink:#0E2436;--ink2:#33475b;--muted:#6b7c8d;--line:#e3e8ee;--paper:#f5f7f9;--card:#fff;
 --teal:#0e7c7b;--teal-d:#0b5f5e;--amber:#b7791f;--amber-bg:#fdf6e7;--red:#c8433b;--red-bg:#fdeceb;
 --green:#1f9d6b;--brand:#6d4aff;--dqc-disp:'Space Grotesk',system-ui,sans-serif;--dqc-mono:'JetBrains Mono',ui-monospace,monospace;
 font-family:'Inter',system-ui,sans-serif;color:var(--ink);font-size:14px}
.dqc-top{display:flex;align-items:flex-start;gap:14px;padding:18px 4px 12px}
.dqc-title{font-family:var(--dqc-disp);font-size:20px;font-weight:600}
.dqc-sub{color:var(--muted);font-size:12.5px;margin-top:2px;max-width:640px}
.dqc-btn{font-weight:600;font-size:13px;border-radius:8px;padding:8px 15px;cursor:pointer;border:1px solid transparent;font-family:inherit}
.dqc-btn.out{background:#fff;border-color:var(--line);color:var(--ink2)} .dqc-btn.out:hover{background:var(--paper)}
.dqc-tabs{display:flex;gap:4px;border-bottom:1px solid var(--line);margin-bottom:16px}
.dqc-tab{background:none;border:0;padding:10px 14px;font-size:13.5px;color:var(--muted);cursor:pointer;border-bottom:2px solid transparent;font-family:inherit}
.dqc-tab.on{color:var(--ink);border-bottom-color:var(--brand);font-weight:600}
.dqc-grid{display:grid;grid-template-columns:232px 1fr;gap:22px;align-items:start}
/* UI.3 — the v2 workspace hands the left column to the field list. Specificity 0,2,0
   beats .dqc-grid, so this is order-independent; the floating-rail rules are NOT, and live
   at the very end of this stylesheet instead. */
.dqc-grid.norail{grid-template-columns:1fr}
.dqc-full{grid-column:1/-1}
.dqc-counts{display:flex;border:1px solid var(--line);border-radius:12px;overflow:hidden;background:var(--card);max-width:720px}
.dqc-counts .c{flex:1;padding:12px 16px;border-right:1px solid var(--line)} .dqc-counts .c:last-child{border-right:0}
.dqc-counts .n{font-family:var(--dqc-mono);font-size:22px;font-weight:600}
.dqc-counts .l{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.07em}
.dqc-counts .rev .n{color:var(--amber)} .dqc-counts .app .n{color:var(--teal)} .dqc-counts .rej .n{color:var(--red)}
.dqc-prog{max-width:720px;height:6px;background:var(--line);border-radius:6px;margin-top:9px;overflow:hidden;display:flex}
.dqc-prog i{display:block;height:100%} .dqc-prog .pa{background:#2f7a4f} .dqc-prog .pc{background:#b7791f} .dqc-prog .pp{background:#43607e} .dqc-prog .pr{background:var(--red)}
.dqc-rail{position:sticky;top:12px;background:var(--card);border:1px solid var(--line);border-radius:14px;padding:6px 8px 10px;box-shadow:0 1px 2px rgba(16,36,54,.03)}
.dqc-rail h4{font-size:10px;letter-spacing:.11em;text-transform:uppercase;color:#9aa6b2;font-weight:700;margin:0;padding:11px 8px 5px} .dqc-rail h4:first-child{margin-top:0}
.dqc-opt{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 10px;border-radius:8px;cursor:pointer;font-size:13px;color:var(--ink2);position:relative;transition:background .12s ease,color .12s ease}
.dqc-opt:hover{background:#f4f7fa;color:var(--ink)}
.dqc-opt.on{background:#eef2f6;color:var(--ink);font-weight:600}
.dqc-opt.on::before{content:"";position:absolute;left:2px;top:7px;bottom:7px;width:3px;border-radius:3px;background:var(--teal)}
.dqc-opt .ct{font-family:var(--dqc-mono);font-size:11px;color:#a4b0bc;font-variant-numeric:tabular-nums}
.dqc-opt:hover .ct{color:#7c8794} .dqc-opt.on .ct{color:var(--teal)}
.dqc-gap{margin:12px 6px 0;border:1px solid #ead9b4;background:#fbf6ea;border-radius:10px;padding:10px 12px}
.dqc-gap b{font-family:var(--dqc-disp);font-size:12px;color:#8a6516} .dqc-gap p{margin:4px 0 0;font-size:11.5px;color:#907546;line-height:1.45}
.dqc-gap.datagap{border-color:#f0cdc8;background:#fdf1ef;cursor:pointer} .dqc-gap.datagap b{color:var(--red)} .dqc-gap.datagap p{color:#9a5750}
.dqc-gap.datagap:hover{background:#fbe6e2}
.dqc-toolbar{display:flex;gap:10px;align-items:center;margin-bottom:12px;flex-wrap:wrap}
.dqc-classtoggle{font-size:11.5px;color:#5f6e7d;background:transparent;border:1px solid var(--line);border-radius:20px;padding:3px 12px;cursor:pointer;transition:all .12s;margin-left:4px}
.dqc-classtoggle:hover{background:#f5f7f9;border-color:#c5d0db}
.dqc-classtoggle.on{color:#0b5f5e;background:#e0f3f1;border-color:#9fe1cb}
.dqc-search{flex:1;min-width:180px;display:flex;align-items:center;gap:8px;background:var(--card);border:1px solid var(--line);border-radius:9px;padding:8px 12px}
.dqc-search input{border:0;outline:0;flex:1;font-size:13px;background:transparent;font-family:inherit}
.dqc-toggle{font-size:12.5px;color:var(--ink2);background:var(--card);border:1px solid var(--line);border-radius:9px;padding:8px 12px;cursor:pointer;font-family:inherit}
.dqc-toggle.on{border-color:var(--red);color:var(--red);background:var(--red-bg)}
.dqc-resultbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:0 2px 12px;font-size:12.5px;color:var(--muted)}
/* ── Phase 2: two-level decision bar, decide popover, review status, version/history ── */
.dqc-gatebar{display:flex;gap:8px;flex-wrap:wrap;margin:0 0 10px}
.dqc-gseg{all:unset;cursor:pointer;position:relative;min-width:104px;padding:10px 14px 9px;border:1px solid var(--line);
 border-radius:12px;background:var(--card);transition:box-shadow .15s,border-color .15s,transform .08s}
.dqc-gseg:hover{box-shadow:0 3px 10px rgba(16,36,54,.07)}
.dqc-gseg.on{border-color:var(--t);box-shadow:0 0 0 2px color-mix(in srgb,var(--t) 20%,#fff)}
.dqc-gseg .n{display:block;font-family:var(--dqc-mono);font-size:20px;font-weight:600;color:var(--t);line-height:1.15}
.dqc-gseg .l{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.06em}
.dqc-gseg.rr{margin-left:auto}
.dqc-rgrp{margin:2px 0 6px}
.dqc-rgrp-h{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;font-weight:700;padding:6px 10px 3px;opacity:.85}
.dqc-decide{all:unset;cursor:pointer;display:inline-flex;align-items:center;gap:7px;font-size:12.5px;padding:7px 13px;
 border-radius:9px;background:#f1f4f7;color:#3b4a59;border:1px solid var(--line)}
.dqc-decide:hover{background:#e8edf2}
.dqc-decide[style*="--t"]{background:color-mix(in srgb,var(--t) 10%,#fff);color:var(--t);border-color:color-mix(in srgb,var(--t) 40%,#fff)}
.dqc-decide .dot{width:8px;height:8px;border-radius:50%;background:var(--t)}
.dqc-decide .cv{opacity:.5;font-size:10px}
.dqc-decide.pending{box-shadow:0 0 0 2px color-mix(in srgb,var(--t) 18%,#fff)}
.dqc-bulk{margin-left:auto}
.dqc-pop-scrim{position:fixed;inset:0;z-index:80;background:rgba(14,36,54,.30)}
.dqc-pop{position:fixed;z-index:81;top:50%;left:50%;transform:translate(-50%,-50%);width:min(520px,94vw);
 background:#fff;border-radius:14px;box-shadow:0 24px 64px rgba(0,0,0,.26);overflow:hidden}
.dqc-pop-h{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:13px 16px;
 background:#0e2436;color:#fff;font-size:13.5px;font-weight:600;font-family:var(--dqc-disp)}
.dqc-pop-h .x{all:unset;cursor:pointer;font-size:22px;line-height:1;color:#cdd8ea}.dqc-pop-h .x:hover{color:#fff}
.dqc-pop-gates{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;padding:14px 16px 4px}
.dqc-pop-gates .gbtn{all:unset;cursor:pointer;text-align:center;padding:9px 6px;border-radius:9px;font-size:12.5px;
 font-weight:600;color:#5a6675;background:#f1f4f7;border:1px solid transparent}
.dqc-pop-gates .gbtn:hover{background:#e8edf2}
.dqc-pop-gates .gbtn.on{background:color-mix(in srgb,var(--t) 13%,#fff);color:var(--t);border-color:var(--t)}
.dqc-pop-body{padding:8px 16px 4px}
.dqc-pop-body .lbl{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:#9aa6b2;font-weight:700;margin:10px 0 6px}
.dqc-pop-body .lbl.req b{color:var(--red)}
.ropt{display:flex;gap:9px;align-items:flex-start;padding:7px 9px;border-radius:8px;cursor:pointer;font-size:13px;color:var(--ink2)}
.ropt:hover{background:#f4f7fa} .ropt.on{background:#eef2f6;color:var(--ink);font-weight:600}
.ropt em{font-style:normal;color:var(--muted);font-weight:400;font-size:11.5px}
.dqc-pop-ta{width:100%;box-sizing:border-box;font:inherit;font-size:13px;padding:9px 11px;border:1px solid var(--line);
 border-radius:9px;resize:vertical}
.dqc-pop-ta:focus{outline:none;border-color:var(--teal);box-shadow:0 0 0 2px #d9ecea}
.dqc-pop-body .warn{font-size:11.5px;color:#a5570f;margin:6px 2px 0}
.dqc-pop-f{display:flex;justify-content:flex-end;gap:9px;padding:12px 16px;border-top:1px solid var(--line);background:#f8fafb}
.dqc-donetag{font-size:10.5px;font-weight:600;color:#fff;border-radius:20px;padding:2px 9px;margin-left:8px}
.dqc-rs{font-size:10.5px;font-weight:600;border-radius:20px;padding:2px 8px;margin-left:6px}
.dqc-rs.rs-re{background:#fbe4d6;color:#a5570f} .dqc-rs.rs-ok{background:#e9f5ee;color:#2f7a4f}
.dqc-ver{display:flex;align-items:center;gap:10px;font-size:12px;color:var(--muted)}
.dqc-ver .v{font-family:var(--dqc-mono);background:#eef1f5;color:#42586e;border-radius:6px;padding:1px 7px;font-weight:600}
.dqc-ver .hlink{all:unset;cursor:pointer;color:#0e7c7b}.dqc-ver .hlink:hover{text-decoration:underline}
.dqc-ver .last{font-size:11.5px}
.dqc-history .h-empty{font-size:12px;color:var(--muted);padding:6px 0}
.dqc-history .ha{color:var(--muted);text-align:right}
.dqc-resultbar .cnt b{color:var(--ink);font-family:var(--dqc-mono)}
.dqc-fpill{display:inline-flex;align-items:center;gap:6px;background:#eef2f6;color:var(--ink2);border-radius:20px;padding:3px 10px;font-size:12px}
.dqc-fpill .x{cursor:pointer;color:var(--muted);font-weight:700}
.dqc-fpill .x:hover{color:var(--red)}
.dqc-fchip{display:inline-flex;align-items:baseline;gap:5px;background:#f2f5f8;border-radius:6px;padding:2px 8px;margin:0 6px 6px 0;font-size:12.5px}
.dqc-code.plain{background:#0f3b3a;color:#d6efec}
pre.plainlogic{font-family:var(--dqc-mono);font-size:12px;background:#0f3b3a;color:#d6efec;padding:12px 14px;border-radius:8px;overflow-x:auto;white-space:pre-wrap;margin:0 0 12px}
.dqc-fhead{margin:14px 2px 8px;display:flex;align-items:baseline;gap:10px}
.dqc-fhead .fh-label{font-family:var(--dqc-disp);font-size:14px;font-weight:700;color:var(--ink)}
.dqc-fhead .fh-tech{font-family:var(--dqc-mono);font-size:11px;color:var(--muted)}
.dqc-fhead .fh-n{margin-left:auto;font-size:11px;color:var(--muted);background:#eef2f5;padding:2px 8px;border-radius:20px}
.dqc-card{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:16px 18px;margin-bottom:12px}
.dqc-card:hover{border-color:#cdd6df}
.dqc-card.done-approved{border-left:3px solid #2f7a4f} .dqc-card.done-conditional{border-left:3px solid #b7791f}
.dqc-card.done-pending{border-left:3px solid #43607e} .dqc-card.done-rejected{border-left:3px solid var(--red);opacity:.78}
.dqc-rid{font-family:var(--dqc-mono);font-size:10.5px;color:#8a97a3;background:#f1f4f7;border-radius:5px;padding:1px 7px;margin-left:8px;font-weight:400;vertical-align:middle}
.dqc-vbadge{font-family:var(--dqc-mono);font-size:10.5px;font-weight:600;color:#0b5f5e;background:#e0f3f1;border-radius:5px;padding:1px 7px;vertical-align:middle}
.vtl-base{font-size:9px;font-weight:700;letter-spacing:.05em;color:#5f6e7d;background:#eef1f5;border:1px solid #d3d9e0;border-radius:20px;padding:1px 7px;text-transform:uppercase}
.dqc-rname{font-family:var(--dqc-disp);font-size:15.5px;font-weight:600;margin-bottom:8px;display:flex;gap:9px;align-items:baseline;flex-wrap:wrap}
.dqc-badges{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:9px}
.dqc-badges .b{font-size:10.5px;font-weight:600;padding:3px 8px;border-radius:20px}
.b.trust.t-confirmed{background:#e6f4ee;color:#0f6b45} .b.trust.t-supported{background:#eaf3fb;color:#1f5fa8}
.b.trust.t-tentative{background:#fdf6e7;color:var(--amber)} .b.trust.t-unverified{background:#f3f0fb;color:#6d4aff}
.b.risk.k-low{background:#eef2f5;color:#42586e} .b.risk.k-moderate{background:#fdf1e3;color:#b7791f} .b.risk.k-high{background:#fdeceb;color:var(--red)}
.b.reason{background:#0e2436;color:#fff} .b.quick{background:#0e7c7b;color:#fff}
.dqc-review{background:#f7f9fb;border:1px solid var(--line);border-radius:10px;padding:12px 14px;margin-bottom:14px}
.dqc-fchip.act{all:unset;cursor:pointer;display:inline-flex;align-items:center;gap:6px;background:#eaf1f6;color:#2c5b7a;
 border:1px solid #cfe0ea;border-radius:20px;padding:3px 11px;font-size:11.5px;margin:0 5px 5px 0}
.dqc-fchip.act:hover{background:#0e7c7b;border-color:#0e7c7b;color:#fff}
.dqc-fchip.act .ic{opacity:.8;font-size:12px}
.dqc-fchip.act .phys{font-family:var(--dqc-mono);opacity:.75}
.dqc-fchip.act:hover .phys{opacity:.9}
.hintk{font-style:normal;font-size:10px;color:#0e7c7b;font-weight:500;letter-spacing:0;text-transform:none}
.dqc-valbtn{all:unset;cursor:pointer;display:inline-flex;align-items:center;gap:6px;background:#0e7c7b;color:#fff;
 border-radius:8px;padding:5px 12px;font-size:12px;font-weight:600;margin:0 6px 6px 0}
.dqc-valbtn:hover{background:#0a6160}
.dqc-mini.vals{border-color:#0e7c7b;color:#0e7c7b;font-weight:600}
.dqc-mini.vals:hover{background:#0e7c7b;color:#fff}
.dqc-smecmt{border-left:3px solid var(--t,#c2ccd6);background:#f8fafb;border-radius:8px;padding:7px 11px;margin:-2px 0 10px}
.dqc-smecmt b{font-size:11.5px;color:var(--t,#42586e)}
.dqc-smecmt .w{font-size:11px;color:var(--muted);margin-left:8px;font-family:var(--dqc-mono)}
.dqc-smecmt .c{font-size:12.5px;color:#33404e;line-height:1.5;font-style:italic;margin-top:3px}
.dqc-ver-timeline{margin-top:12px;border-top:1px solid var(--line);padding-top:10px}
.ver-head{display:flex;align-items:center;gap:12px;margin-bottom:8px}
.ver-cur{font-size:13px;font-weight:700;color:#0E2436;font-family:ui-monospace,monospace}
.ver-tl{position:relative;padding-left:18px}
.ver-tl::before{content:'';position:absolute;left:6px;top:12px;bottom:12px;width:2px;background:#e3e8ee}
.ver-item{position:relative;padding:8px 0 12px;display:flex;gap:12px}
.ver-item.active{background:#f6fafa;margin:-2px -8px;padding:10px 8px;border-radius:9px}
.ver-dot{width:12px;height:12px;border-radius:50%;background:#e3e8ee;border:2px solid #fff;flex-shrink:0;margin-top:3px;z-index:1}
.ver-item.active .ver-dot{background:#0e7c7b;box-shadow:0 0 0 3px rgba(14,124,123,.2)}
.ver-body{flex:1;min-width:0}
.ver-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12px}
.vv{font-family:ui-monospace,monospace;font-weight:700;color:#42586e;background:#eef1f5;border-radius:6px;padding:1px 7px}
.ver-act{font-size:9px;font-weight:700;letter-spacing:.05em;color:#fff;background:#0e7c7b;border-radius:20px;padding:1px 7px}
.ver-type{color:#0b5f5e;font-size:11.5px}
.ver-when{color:var(--muted);font-size:11px;margin-left:auto;font-family:ui-monospace,monospace}
.ver-sum{font-size:12px;color:#33404e;margin-top:3px;line-height:1.5}
.ver-imp{font-size:12px;margin-top:4px;font-family:ui-monospace,monospace}
.ver-imp .g{color:#2f7a4f}.ver-imp .r{color:#b23a30}
.ver-diff{margin-top:5px;font-size:11.5px}
.ver-diff summary{cursor:pointer;color:#0e7c7b}
.vd-row{display:grid;grid-template-columns:100px 1fr 20px 1fr;gap:4px;padding:3px 0;font-family:ui-monospace,monospace;font-size:11px}
.vd-k{color:#5a6675;font-weight:600}.vd-from{color:#8a3f38}.vd-arr{color:#9aa6b2;text-align:center}.vd-to{color:#2a5e3d}
.dqc-smebox{border-left:3px solid var(--t);background:#f8fafb;border-radius:9px;padding:10px 13px;margin-bottom:10px}
.dqc-smebox.none{--t:#c2ccd6}
.dqc-smebox .hd{display:flex;align-items:center;gap:9px;flex-wrap:wrap}
.dqc-smebox .gate{font-size:10px;font-weight:700;letter-spacing:.07em;color:#fff;background:var(--t);border-radius:20px;padding:2px 9px}
.dqc-smebox .cat{font-size:13px;font-weight:600;color:var(--ink)}
.dqc-smebox .who{margin-left:auto;font-size:11.5px;color:var(--muted);font-family:var(--dqc-mono)}
.dqc-smebox .cmt{margin:7px 0 0;font-size:13px;color:#33404e;line-height:1.55;font-style:italic}
.dqc-revised{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;font-weight:600;border-radius:20px;
 padding:2px 9px;margin-left:6px;background:#eef2f8;color:#43607e}
.dqc-dec{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:600;border-radius:20px;
 padding:2px 9px;margin-left:8px;background:color-mix(in srgb,var(--t) 12%,#fff);color:var(--t)}
.dqc-dec .dot{width:7px;height:7px;border-radius:50%;background:var(--t)}
.dqc-triage{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px}
.dqc-triage .chip{font-size:11.5px;padding:4px 10px;border-radius:20px;background:#fff;border:1px solid var(--line);color:var(--ink)}
.dqc-triage .chip b{font-weight:700;margin-right:4px;color:var(--muted);text-transform:uppercase;font-size:9.5px;letter-spacing:.05em}
.dqc-triage .chip.trust.t-confirmed{background:#e6f4ee} .dqc-triage .chip.trust.t-tentative{background:#fdf6e7} .dqc-triage .chip.trust.t-unverified{background:#f3f0fb} .dqc-triage .chip.trust.t-supported{background:#eaf3fb}
.dqc-triage .chip.risk.k-high{background:#fdeceb} .dqc-triage .chip.risk.k-moderate{background:#fdf1e3}
.dqc-triage .chip.quick{background:#0e7c7b;color:#fff;border-color:#0e7c7b}
.dqc-todo{background:#fff;border-left:3px solid var(--dqc-brand);padding:8px 12px;border-radius:6px;font-size:13px;margin:6px 0 10px}
.dqc-triexplain{font-size:12px;color:var(--ink2);display:grid;gap:4px;margin-top:6px;opacity:.92}
.dqc-rejreason{font-size:12.5px;color:var(--red);background:#fdeceb;border-radius:6px;padding:6px 10px;margin-right:8px}
.b.fam{background:#eef1f6;color:#42586e} .b.xt{background:#e7f1f1;color:var(--teal-d)} .b.xf{background:#eef2f6;color:#4a6076}
.b.dim{border:1px solid transparent}
.b.dim-completeness{background:#e9f5ee;color:#2f7a4f} .b.dim-validity{background:#eef2f8;color:#43607e}
.b.dim-conformity{background:#eef1f5;color:#586675} .b.dim-consistency{background:#e2f1ef;color:#0b5f5e}
.b.dim-integrity{background:#faedcf;color:#946011} .b.dim-uniqueness{background:#f5f0e4;color:#8a6d1c}
.b.auth-confirmed{background:#e6f4ee;color:#0f6b45} .b.auth-observed{background:#eaf1f8;color:#2c5a8c}
.b.auth-inferred{background:#f4eefb;color:#6a3fb0} .b.auth-none{background:#f2f2f2;color:#777}
.b.conf{font-family:var(--dqc-mono);background:#f5f7f9;color:var(--ink2)}
.b.strength{background:#eef2f6;color:#5a6b7d;text-transform:lowercase}
.b.caveat{background:var(--amber-bg);color:var(--amber)}
/* review-workflow badges — soft-tinted to match the house pill style, no solid fills */
.dqc-badges .b{letter-spacing:.005em;line-height:1.55;display:inline-flex;align-items:center;gap:3px}
.b.rev-obj{background:#fbe9e7;color:#b23a30}
.b.rev-refined{background:#eef2f8;color:#43607e}
.b.rev-ok{background:#e9f5ee;color:#2f7a4f}
.b.suspect{background:#faedcf;color:#946011}
.b.clientok{background:#e6f4ee;color:#0f6b45}
.b.clientval{background:#e2f1ef;color:var(--teal-d)}
.b.clientpend{background:#f5f0e4;color:#8a6d1c}
/* quiet the 'what to do' chip — it was a solid dark block that dominated the card */
.b.reason{background:#eef1f6;color:#37506c;border:1px solid #dde4ec;font-weight:600}
.b.quick{background:#e3f1ef;color:var(--teal-d);border:1px solid #cfe6e2}
/* accurate one-line summary under each rule */
.dqc-note{font-size:12px;color:#586675;margin:-1px 0 11px;padding:7px 11px;background:#f6f8fa;border:1px solid #eef1f5;border-radius:8px;line-height:1.5}
/* card depth + calmer hover */
.dqc-card{box-shadow:0 1px 2px rgba(16,36,54,.035);transition:box-shadow .16s ease,border-color .16s ease}
.dqc-card:hover{border-color:#cfd8e1;box-shadow:0 4px 14px rgba(16,36,54,.07)}
/* collapsible sidebar sections */
.dqc-sec{margin:0}
.dqc-sec + .dqc-sec{border-top:1px solid #eef1f4}
.dqc-sec-h{user-select:none;transition:color .12s,background .12s;cursor:pointer;display:flex;align-items:center;justify-content:space-between;border-radius:7px;padding:9px 8px 8px}
.dqc-sec-h:hover{background:#f4f7fa;color:var(--ink2)}
.dqc-sec-h .chev{opacity:.5;font-size:8px;transition:transform .15s;color:#9aa6b2}
.dqc-clientok{margin-top:10px;padding:9px 12px;border-left:3px solid #1f9d6b;background:#f1faf5;border-radius:8px}
.dqc-clientok .k{margin-bottom:3px;color:#0f6b45;font-weight:600;font-size:12.5px}
.dqc-clientok .dqc-p{margin:2px 0}
.dqc-clientok code{font-family:var(--dqc-mono);font-size:11.5px;background:#e6f4ee;padding:1px 5px;border-radius:4px}
.dqc-clientok.pending{border-left-color:#b7791f;background:#faf6ec}
.dqc-clientok.pending .k{color:#8a6d1c}
.dqc-clientok.pending code{background:#f0e8d5}
/* collapsible sample records in the detail modal */
.dqc-samples{margin-top:12px}
.dqc-sampletoggle{all:unset;cursor:pointer;display:flex;align-items:center;gap:9px;width:100%;box-sizing:border-box;
 padding:9px 12px;border:1px solid var(--line);border-radius:9px;background:var(--paper);font-size:13px;color:var(--ink2)}
.dqc-sampletoggle:hover{background:#eef2f6}
.dqc-sampletoggle .chev{opacity:.5;font-size:10px}
.dqc-sampletoggle .dqc-samplecount{display:inline-flex;gap:6px}
.dqc-sampletoggle em{font-style:normal;font-size:11px;font-weight:600;padding:1px 8px;border-radius:20px}
.dqc-sampletoggle em.f{background:#fbe9e7;color:#b23a30} .dqc-sampletoggle em.p{background:#e9f5ee;color:#2f7a4f}
.dqc-sampletoggle .hint{margin-left:auto;font-size:11.5px;color:var(--muted)}
.dqc-samplebody{border:1px solid var(--line);border-top:none;border-radius:0 0 9px 9px;padding:2px 12px 10px}
.dqc-dispo{margin:10px 0 0;font-size:12.5px;color:var(--amber);background:var(--amber-bg);padding:8px 11px;border-radius:8px}
.dqc-p{font-size:13.5px;color:var(--ink2);margin:0 0 4px}
.dqc-ul{margin:0;padding-left:18px;font-size:13px;color:var(--ink2)} .dqc-ul li{margin:3px 0}
.dqc-caveat{border:1px solid #eccf8f;background:var(--amber-bg);border-radius:10px;padding:12px 14px;margin:6px 0 4px}
.dqc-genrat{border:1px solid #bfe0d8;background:#eef7f4;border-radius:10px;padding:12px 14px;margin:6px 0 4px}
.dqc-genrat .ch{font-weight:600;font-size:13px;margin-bottom:6px;color:#0b5f5e}
.dqc-genrat p{font-size:12.5px;color:var(--ink1);margin:4px 0;line-height:1.45}
.dqc-genrat p.rat{color:var(--ink2)}
.dqc-genrat p.meta{color:var(--muted);font-size:11.5px}
.dqc-caveat .ch{font-family:var(--dqc-disp);font-weight:600;font-size:13px;color:#8a5a12;display:flex;gap:8px;align-items:center}
.dqc-caveat .verdict{font-family:var(--dqc-mono);font-size:10.5px;background:#f0dcae;color:#7a4d0d;padding:2px 7px;border-radius:20px;text-transform:uppercase}
.dqc-caveat p{margin:7px 0 0;font-size:13px;color:#6b4e12} .dqc-caveat .rat{color:#7a5a2a;font-size:12.5px}
.dqc-ev{font-size:13px;color:var(--ink2);margin:0 0 5px}
.dqc-dialect{font-family:var(--body);font-size:12px;border:1px solid var(--line);border-radius:6px;padding:3px 8px;color:var(--ink2);background:#fff;text-transform:none;letter-spacing:0}
.dqc-why{color:var(--ink2);font-size:13px;margin-bottom:12px}
.dqc-subtitle{color:var(--ink1);font-size:13.5px;font-weight:500;margin:-2px 0 8px;line-height:1.4}
.dqc-meta{display:flex;gap:18px;flex-wrap:wrap;font-size:12px;color:var(--muted);margin-bottom:12px}
.dqc-meta b{color:var(--ink2)} .dqc-meta .novel{color:var(--amber);font-weight:600}
.dqc-meta .phys{font-family:var(--dqc-mono);font-size:11px;color:#9aa8b5;margin-left:4px}
.dqc-impact{background:var(--paper);border-radius:10px;padding:11px 13px;margin-bottom:13px}
.dqc-impact .lab{font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin-bottom:7px;display:flex;justify-content:space-between}
.dqc-ibar{height:22px;border-radius:5px;overflow:hidden;display:flex;background:#e9edf1}
.dqc-ibar i{display:block;height:100%} .dqc-ibar .pass{background:var(--green)} .dqc-ibar .flag{background:var(--red)}
.dqc-iline{font-family:var(--dqc-mono);font-size:12px;margin-top:7px;color:var(--ink2)}
.dqc-iline .r{color:var(--red);font-weight:600} .dqc-iline .g{color:var(--green);font-weight:600}
.dqc-impact.un{border:1px dashed var(--line);background:#fafbfc;display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.dqc-impact.un .lab{margin:0;text-transform:none;letter-spacing:0;font-size:12px;color:var(--ink2);font-weight:600}
.dqc-impact.un .untext{font-size:12px;color:var(--muted)}
.dqc-mini{margin-left:auto;font-family:inherit;font-size:12px;font-weight:600;color:var(--teal);background:#fff;border:1px solid #bfe0df;border-radius:7px;padding:6px 12px;cursor:pointer}
.dqc-mini:hover{background:#eff7f6}
.dqc-acts{display:flex;gap:9px;align-items:center}
.dqc-btn.app{background:var(--teal);color:#fff} .dqc-btn.app:hover{background:var(--teal-d)}
.dqc-btn.rej{background:#fff;color:var(--red);border-color:#f0c9c6} .dqc-btn.rej:hover{background:var(--red-bg)}
.dqc-btn.info{background:#fff;color:var(--ink2);border-color:var(--line)} .dqc-btn.info:hover{background:var(--paper)}
.dqc-expand{margin-left:auto;font-size:12px;color:var(--muted);cursor:pointer;font-family:var(--dqc-mono)}
.dqc-detail{margin-top:13px;border-top:1px solid var(--line);padding-top:12px;display:none}
.dqc-detail.open{display:block}
.dqc-detail .k{font-size:10.5px;letter-spacing:.07em;text-transform:uppercase;color:var(--muted);margin-bottom:5px}
.dqc-detail pre,.dqc-code{font-family:var(--dqc-mono);font-size:12px;background:var(--ink);color:#d7e6f2;padding:12px 14px;border-radius:8px;overflow-x:auto;white-space:pre-wrap;margin:0 0 12px}
.dqc-donetag{font-family:var(--dqc-mono);font-size:11px;padding:3px 9px;border-radius:6px}
.dqc-donetag.a{background:#e6f4ee;color:var(--teal-d)} .dqc-donetag.r{background:var(--red-bg);color:var(--red)}
.dqc-tray{position:fixed;left:0;right:0;bottom:0;background:var(--ink);color:#fff;display:flex;align-items:center;gap:14px;padding:12px 24px;box-shadow:0 -4px 24px rgba(14,36,54,.2);z-index:40}
.dqc-tray .t{font-family:var(--dqc-mono);font-size:13px}
.dqc-tray .undo{background:transparent;color:#9db4c9;border:1px solid #33475b;border-radius:8px;padding:9px 16px;cursor:pointer;font-family:inherit}
.dqc-tray .save{background:var(--teal);color:#fff;font-weight:600;border:0;border-radius:8px;padding:9px 20px;cursor:pointer;font-family:inherit}
.dqc-empty{text-align:center;color:var(--muted);padding:50px 20px}
.dqc-flash{position:fixed;bottom:74px;left:50%;transform:translateX(-50%);background:var(--ink);color:#fff;padding:10px 18px;border-radius:10px;font-size:13px;z-index:50}
.dqc-pillrow{display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap}
.dqc-pill{background:#fff;border:1px solid var(--line);border-radius:20px;padding:6px 14px;cursor:pointer;font-size:13px;font-family:inherit}
.dqc-pill.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.dqc-tablewrap{border:1px solid var(--line);border-radius:12px;overflow:hidden;background:#fff}
.dqc-table{width:100%;border-collapse:collapse;font-size:13px}
.dqc-table th{text-align:left;padding:10px 14px;background:var(--paper);color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.06em}
.dqc-table td{padding:10px 14px;border-top:1px solid var(--line)}
.dqc-trow{cursor:pointer} .dqc-trow:hover{background:var(--paper)}
.dqc-panel{border:1px solid var(--line);border-radius:12px;padding:14px;background:#fff}
.dqc-panel h5{margin:12px 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:var(--muted)}
.dqc-topval{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px dotted var(--line);font-size:13px}
.dqc-muted{color:var(--muted);font-size:12px}
.dqc-modalbg{position:fixed;inset:0;background:rgba(14,36,54,.5);display:grid;place-items:center;z-index:2000;padding:16px}
.dqc-modal{background:#fff;border-radius:14px;width:min(640px,96vw);max-height:88vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.3)}
.dqc-modal.wide{width:min(1560px,98vw);max-height:calc(100vh - 32px)}
/* the header stays visible while the details scroll; Accenture styling (core purples) */
.dqc-modal .dqc-modalh{position:sticky;top:0;z-index:5;background:linear-gradient(90deg,#460073 0%,#7500C0 55%,#A100FF 100%) !important;
  border-bottom:3px solid #A100FF;color:#fff}
.dqc-modal .dqc-modalh .dqc-muted{color:#e9d7ff !important;letter-spacing:.08em}
.dqc-modal .dqc-modalh .dqc-muted::before{content:">";color:#fff;font-weight:800;margin-right:6px}
.rd2-de{color:#5b6b78;font-style:normal}
.rd2-link{all:unset;cursor:pointer;color:#7500C0;font-weight:600;text-decoration:underline;text-underline-offset:2px}.rd2-link:hover{color:#A100FF}
.rd2-over .fv-scrim,.rd2-over .vh-scrim{z-index:2500}
/* RD2 — rule details: recommendation, main (what / data / concerns / why) | side (decision / basis / fields / sql / history) */
.rd2{padding:14px 18px}
.dqc-satag{font-size:11px;font-weight:600;border-radius:10px;padding:1px 8px;border:1px solid #d6dbe1;background:#f3f4f6;color:#4b5563;white-space:nowrap}
.dqc-satag.reference_gap{background:#fbf6ea;color:#8a5a00;border-color:#ead9b4}.dqc-satag.data_issue,.dqc-satag.clean_control{background:#f1f8f3;color:#2f7a4f;border-color:#bfe0cb}
.dqc-satag.likely_wrong{background:#fdf1ef;color:#9b2b1f;border-color:#f3c2bb}.dqc-satag.no_value,.dqc-satag.duplicate,.dqc-satag.too_few_rows{background:#f3f4f6;color:#6b7280}
.rd2-rec.sa{border-left:4px solid #8a97a3;background:#f7f8fa}.rd2-rec.sa.reference_gap{border-color:#c98a1b;background:#fbf6ea}
.rd2-rec.sa.data_issue{border-color:#2f7a4f;background:#f1f8f3}.rd2-rec.sa.clean_control{border-color:#2f7a4f;background:#f1f8f3}
.rd2-rec.sa.likely_wrong{border-color:#c0392b;background:#fdf1ef}.rd2-rec.sa.no_value,.rd2-rec.sa.duplicate{border-color:#8a97a3}
.rd2-sa-ev{margin:6px 0 0;padding-left:18px;font-size:12.5px;color:#4b5563}.rd2-sa-ev li{margin:2px 0}
.rd2-rec{border-radius:8px;padding:10px 14px;margin-bottom:14px;font-size:13.5px;line-height:1.45;border-left:4px solid #8a97a3;background:#f4f6f8}
.rd2-rec.ok{border-color:#2f7a4f;background:#eef7f1}.rd2-rec.warn{border-color:#c0392b;background:#fdf1f0}.rd2-rec.info{border-color:#c98a1b;background:#fdf6e9}.rd2-rec.rev{border-color:#43607e;background:#eef2f8}
.rd2-grid{display:grid;grid-template-columns:minmax(0,1.65fr) minmax(330px,1fr);gap:18px;align-items:start}
@media (max-width:980px){.rd2-grid{grid-template-columns:1fr}}
.rd2-sec{border:1px solid #e6eaee;border-radius:10px;padding:12px 14px;margin-bottom:12px;background:#fff}
.rd2-sec.warn{border-color:#f0c9c4;background:#fffaf9}
.rd2-h{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#5b6b78;font-weight:700;margin-bottom:8px}
.rd2-h .hintk{font-weight:400;text-transform:none;letter-spacing:0;margin-left:6px}
.rd2-note{color:#43607e;font-size:12.5px;margin:6px 0}
.rd2-side .dqc-review{margin-bottom:12px}
.rd2-fields{display:flex;flex-wrap:wrap;gap:6px}
.rd2-hist{margin-top:4px}.rd2-hist .dqc-histrow{flex-direction:column}
.dqc-mgrid{display:grid;grid-template-columns:1fr 1fr;gap:22px;margin-top:12px}
@media (max-width:820px){.dqc-mgrid{grid-template-columns:1fr}}
.dqc-mcol{min-width:0}
.dqc-mcol .k{margin-top:16px}
.dqc-mcol .k:first-child{margin-top:0}
.dqc-plainlogic{background:#f2f7f5;border-left:3px solid var(--dqc-teal,#0e7c7b);border-radius:8px;padding:12px 14px;font-size:14px;line-height:1.5;color:var(--ink)}
.dqc-pseudo{background:#0e2436;color:#d7e5f5;border-radius:8px;padding:12px 14px;font-family:var(--dqc-mono);font-size:12.5px;line-height:1.55;white-space:pre-wrap;word-break:break-word;overflow:auto;margin:0}
.dqc-kv{display:grid;gap:0}
.dqc-kv .kv{display:grid;grid-template-columns:130px 1fr;gap:12px;padding:8px 0;border-bottom:1px solid var(--line)}
.dqc-kv .kv:last-child{border-bottom:none}
.dqc-kv .kk{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:var(--ink2);font-weight:600}
.dqc-kv .kvv{font-size:13px;color:var(--ink)}
.dqc-modalh{display:flex;justify-content:space-between;align-items:flex-start;padding:18px 20px;background:var(--ink);color:#fff}
.dqc-x{background:none;border:0;color:#fff;font-size:26px;cursor:pointer;line-height:1}
.dqc-modalbody{padding:18px 20px}
.dqc-modalbody .k{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--ink2);font-weight:600;margin:16px 0 7px}
.dqc-modalbody .k:first-child{margin-top:0}
.dqc-kpis{display:flex;gap:22px} .dqc-kpis>div{display:flex;flex-direction:column}
.dqc-kpis span{font-family:var(--dqc-mono);font-size:20px;font-weight:600} .dqc-kpis .r{color:var(--red)} .dqc-kpis .g{color:var(--green)}
.dqc-kpis small{color:var(--muted);font-size:11px}
.dqc-row2{display:flex;gap:14px;padding:5px 0;font-size:13px} .dqc-row2 .rk{color:var(--muted);min-width:120px} .dqc-row2 .rv{color:var(--ink2);flex:1}
.dqc-modalf{display:flex;justify-content:flex-end;gap:10px;padding:14px 20px;border-top:1px solid var(--line);position:sticky;bottom:0;background:#fff}
@media(max-width:820px){.dqc-grid{grid-template-columns:1fr}.dqc-rail{position:static}}

/* UI.3 — floating facet rail (v2). These MUST stay last: .dqc-rail-pop and .dqc-rail carry
   equal specificity, and the 820px rule above resets .dqc-rail to position:static. A popover
   that silently reverts to a sticky column on a narrow screen is worse than no popover, so
   these are placed after every rule that could otherwise win on source order. */
.dqc-rail.dqc-rail-pop{position:fixed;z-index:81;top:50%;left:50%;transform:translate(-50%,-50%);
 width:min(320px,94vw);max-height:82vh;display:flex;flex-direction:column;padding:0;overflow:hidden;
 box-shadow:0 24px 64px rgba(0,0,0,.26)}
.dqc-rail-poph{display:flex;align-items:center;gap:10px;padding:12px 14px;background:#0e2436;color:#fff;
 font-size:13.5px;font-family:var(--dqc-disp);flex-shrink:0}
.dqc-rail-poph b{flex:1;font-weight:600}
.dqc-rail-poph .clr{all:unset;cursor:pointer;font-size:11.5px;color:#9db4c9}.dqc-rail-poph .clr:hover{color:#fff}
.dqc-rail-poph .x{all:unset;cursor:pointer;font-size:22px;line-height:1;color:#cdd8ea}.dqc-rail-poph .x:hover{color:#fff}
.dqc-rail-popb{overflow-y:auto;padding:2px 8px 10px}

/* UI.3.2 — provenance chip on the rule card */
.dqc-made{display:flex;gap:6px;flex-wrap:wrap;margin:5px 0 0}
.dqc-made .m{font-size:10.5px;padding:1px 8px;border-radius:20px;border:1px solid var(--line);
 color:#5f6e7d;background:#fff;white-space:nowrap}
.dqc-made .m.llm{color:#6b3fa0;border-color:#d8cbe9;background:#f6f2fb}
.dqc-made .m.ek{color:#1d6fa5;border-color:#c5dcea;background:#f1f7fb}
.dqc-made .m.sdh{color:#1f7a63;border-color:#bfded4;background:#f0f8f5}

/* version timeline (rule content changes) */
.dqc-section-v,.dqc-section-s{border-top:1.5px solid var(--line);padding-top:14px;margin-top:2px}
.dqc-hbadges{margin-top:9px;display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.dqc-hbadges .hb{font-size:10px;font-weight:600;border-radius:20px;padding:2px 9px;background:#1a3548;color:#a8c4dc}
.dqc-hbadges .hb.ver{font-family:var(--dqc-mono);background:#1a4a6b;color:#9fd4f0;border-radius:5px}
.dqc-hbadges .hb.rev{background:#3a3320;color:#e8d89a}
.dqc-hbadges .hb.dim{background:#243f36;color:#9fe1cb}
.dqc-histrow{display:flex;flex-direction:column;gap:9px;padding:14px 20px;border-top:1.5px solid var(--line);margin-top:2px}
/* F6 additions */
.dqc-hbadges .hb.conf{font-weight:600}
.dqc-hbadges .hb.conf-g{background:#e0f3f1;color:#0b5f5e;border-color:#9fe1cb}
.dqc-hbadges .hb.conf-s{background:#fbf6ea;color:#8a6516;border-color:#ead9b4}
.dqc-hbadges .hb.conf-sl{background:#eef1f4;color:#5f6e7d;border-color:#d5dde4}
.dqc-plain{margin:14px 0 6px}
.dqc-plain .dqc-p.big{font-size:14.5px;line-height:1.55;color:var(--ink1)}
.dqc-why2{border:1px solid #d7e6e2;background:#f2f9f7;border-radius:12px;padding:14px 16px;margin:8px 0 14px}
.dqc-why2 .k{margin-top:0}
.dqc-why2 .sub{font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--ink2);font-weight:600;margin:10px 0 3px}
.dqc-why2 .agentbox{border-left:3px solid #0b8a7d;background:#eaf6f3;border-radius:8px;padding:9px 12px;margin:10px 0 4px}
.dqc-why2 .agentbox .ch{font-weight:600;font-size:12.5px;color:#0b5f5e;margin-bottom:4px}
.dqc-why2 .agentbox p{font-size:12.5px;margin:4px 0;line-height:1.45;color:var(--ink1)}
.dqc-why2 .agentbox p.rat{color:var(--ink2)}
.dqc-why2 .confrow{display:flex;align-items:center;gap:10px;margin-top:10px}
.dqc-why2 .confbadge{font-weight:700;font-size:12px;padding:3px 12px;border-radius:20px}
.dqc-why2 .confbadge.c-g{background:#0b5f5e;color:#fff}
.dqc-why2 .confbadge.c-s{background:#caa64a;color:#2c2410}
.dqc-why2 .confbadge.c-sl{background:#9aa7b2;color:#1e2833}
.dqc-why2 .confwhy{font-size:12px;color:var(--ink2)}
.dqc-sqlwrap{margin-top:14px}
.dqc-sqltoggle{display:flex;align-items:center;gap:8px;width:100%;background:none;border:none;padding:6px 0;cursor:pointer;font-size:13px;color:var(--ink1);font-weight:600;font-family:var(--dqc-disp)}
.dqc-sqltoggle .chev{color:var(--muted)}
.dqc-histsec{padding:14px 20px 0;border-top:1.5px solid var(--line);margin-top:2px}
.dqc-histsec .dqc-histrow{border-top:none;padding:0 0 10px;margin-top:0}
.dqc-histcard{all:unset;cursor:pointer;display:flex;align-items:center;gap:11px;padding:11px 14px;border:1px solid var(--line);border-radius:10px;background:#f8fafb;transition:all .12s}
.dqc-histcard:hover{background:#eef3f6;border-color:#c5d0db}
.dqc-histcard>i{font-size:17px;color:#5f6e7d}
.dqc-histcard .hc-body{flex:1;min-width:0}
.dqc-histcard .hc-title{font-size:13.5px;font-weight:600;color:#0E2436}
.dqc-histcard .hc-sub{font-size:12px;color:#5f6e7d;margin-top:1px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dqc-histcard .hc-v{font-family:var(--dqc-mono);color:#0e7c7b;font-weight:600}
.dqc-histcard .hc-open{font-size:12px;color:#0e7c7b;font-weight:500;white-space:nowrap;display:inline-flex;align-items:center;gap:4px}
.dqc-hp-bg{position:fixed;inset:0;background:rgba(14,36,54,.5);display:grid;place-items:center;z-index:70;padding:20px}
.dqc-hp{background:#fff;border-radius:12px;width:min(560px,96vw);max-height:82vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.3);overflow:hidden}
.dqc-hp-h{display:flex;align-items:center;gap:8px;padding:13px 16px;background:#f7f9fb;border-bottom:1px solid var(--line);font-size:14px;font-weight:600;color:#42586e}
.dqc-hp-h>i{font-size:15px;color:#8a97a3}
.dqc-hp-sub{font-weight:400;color:#8a97a3;font-size:11.5px}
.dqc-hp-x{all:unset;cursor:pointer;margin-left:auto;font-size:18px;color:#8a97a3;line-height:1}
.dqc-hp-body{padding:16px;overflow-y:auto}
.dqc-sec-h{display:flex;align-items:center;gap:8px;padding:0 20px 10px;font-size:12px;font-weight:700;color:#42586e}
.dqc-sec-sub{font-weight:400;color:var(--muted);font-size:11.5px}
.dqc-icon{font-style:normal;font-size:15px;color:#8a97a3}
.vtl,.stl{position:relative;padding-left:24px}
.vtl-line,.stl-line{position:absolute;left:28px;top:18px;bottom:18px;width:2px;background:#e3e8ee}
.vtl-item{position:relative;padding:8px 0 14px;display:flex;gap:14px}
.vtl-item.active{background:#f6fafa;margin:-2px -8px;padding:10px 8px 16px;border-radius:9px}
.vtl-dot{width:14px;height:14px;border-radius:50%;border:2.5px solid #e3e8ee;background:#fff;flex-shrink:0;z-index:1;margin-top:3px}
.vtl-dot.on{border-color:#0e7c7b;background:#0e7c7b}
.vtl-body{flex:1;min-width:0}
.vtl-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12px;margin-bottom:4px}
.vtl-vn{font-family:var(--dqc-mono);font-weight:700;color:#42586e;background:#eef1f5;border-radius:6px;padding:1px 8px}
.vtl-vn.act{background:#e0f3f1;color:#0b5f5e}
.vtl-badge{font-size:9px;font-weight:700;letter-spacing:.05em;color:#fff;background:#0e7c7b;border-radius:20px;padding:1px 7px}
.vtl-type{color:#0b5f5e;font-size:11.5px}
.vtl-when{color:var(--muted);font-size:11px;margin-left:auto;font-family:var(--dqc-mono)}
.vtl-sum{font-size:12.5px;color:#33404e;line-height:1.6}
.vtl-impact{font-size:12px;margin-top:4px;font-family:var(--dqc-mono)}
.vtl-impact .g{color:#2f7a4f}.vtl-impact .r{color:#b23a30}
.vtl-diff{margin-top:5px;font-size:11.5px}
.vtl-diff summary{cursor:pointer;color:#0e7c7b;font-weight:600}
.vtl-d{padding:4px 0;border-bottom:1px solid #f2f4f7;font-family:var(--dqc-mono);font-size:11px}
.vtl-dk{font-weight:700;color:#5a6675;font-size:10px;letter-spacing:.03em;text-transform:uppercase;display:block;margin-bottom:2px}
.vtl-from{color:#8a3f38;padding-left:12px}.vtl-from::before{content:'−  ';font-weight:700}
.vtl-to{color:#2a5e3d;padding-left:12px}.vtl-to::before{content:'+  ';font-weight:700}

/* status & feedback timeline (decisions, comments) */
.stl-item{position:relative;padding:8px 0 14px;display:flex;gap:14px}
.stl-dot{width:14px;height:14px;border-radius:50%;border:2px solid #e3e8ee;background:#fff;flex-shrink:0;z-index:1;margin-top:3px}
.stl-body{flex:1;min-width:0;border-left:3px solid #e3e8ee;padding-left:12px;border-radius:0}
.stl-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12px;margin-bottom:4px}
.stl-gate{font-size:9.5px;font-weight:700;letter-spacing:.05em;color:#fff;border-radius:20px;padding:2px 10px}
.stl-label{font-size:13px;font-weight:600}
.stl-v{font-size:11px;color:var(--muted);font-family:var(--dqc-mono);background:#eef1f5;border-radius:5px;padding:1px 7px}
.stl-when{color:var(--muted);font-size:11px;margin-left:auto;font-family:var(--dqc-mono)}
.stl-cmt{margin:0;font-size:13px;color:#33404e;font-style:italic;line-height:1.6}
`;

/** the codes a rule's logic tests on one field (values of in / not_in / contains_* atoms) - the popup's "accepted" */
function codesOfField(rule: any, fid: string): string[] | undefined {
  const lg = (rule as any)?.logic || (rule as any)?._raw?.logic; if (!lg) return undefined;
  const out = new Set<string>(); const F = String(fid).toUpperCase();
  const walk = (c: any) => { if (!c || typeof c !== "object") return;
    (c.all || []).forEach(walk); (c.any || []).forEach(walk); if (c.not) walk(c.not);
    if (String(c.field || "").toUpperCase() === F && ["in", "contains_any", "contains_all"].includes(String(c.op)))
      (c.values || []).forEach((v: any) => out.add(String(v))); };
  walk(lg.when); walk(lg.then); walk(lg.assert);
  return out.size ? Array.from(out) : undefined;
}
